import { useState, useEffect, useMemo, useCallback, useRef, useDeferredValue } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  MenuItem,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Pagination,
  FormControlLabel,
  Switch,
  InputAdornment
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StorageIcon from '@mui/icons-material/Storage';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LabelIcon from '@mui/icons-material/Label';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Tooltip from '@mui/material/Tooltip';
import useObserveTripSheets from '../../hooks/useSubscribeTripsheet';
import { useCurrentUser, useDatastore, usePhasedModelGate } from '../../hooks';
import { useAlert } from '../../hooks';
import DataLoadingNotice from '../../components/DataLoadingNotice';
import dayjs from 'dayjs';
import {
  addASNToSSCC,
  addASNToUnassignedSSCC,
  addSSCCToManifest,
  deleteASN,
  deleteManifest,
  deleteSSCC,
  deleteUnassignedSSCC,
  markManifestCompleted,
  markSSCCCompleted,
  markUnassignedSSCCCompleted
} from '../../services/adminManifestActions';
import { ReceiptEventPackageProps } from '../../datastore/ssccs';
import useObserveSsccs from '../../hooks/useSubscribeSsccs';
import { dateFormat } from '../../common/dateTimes';
import { SsccStatus } from '../../common';
import {
  AsnStatus,
  getTripDisplayStatus,
  ManifestStatus,
  mapSsccStatusToAsnStatus,
  mapTripLegStatusToSsccStatus,
  TripLegStatus
} from '../../common/status';
import { ManifestProps, TripSheetProps } from '../../datastore/tripSheets';
import {
  MarkCompletedSSCCDialog,
  MarkCompletedManifestDialog,
  DeleteManifestDialog,
  DeleteSSCCDialog,
  DeleteUnassignedSSCCDialog,
  DeleteASNDialog,
  AddSSCCToManifestDialog,
  AddASNToSSCCDialog,
  ConfirmationDialog,
  AddASNForUnassignedSSCCDialog,
  MarkCompletedUnassignedSSCCDialog
} from './dialogs';
import { SsccActionsDrawer } from './components/SsccActionsDrawer';
import { UnassignedSsccCard } from './components/UnassignedSsccCard';
import { getTimeBasedOnStatus, transformManifest, buildAsnKey } from './utils/manifestUtils';
import { useDuplicateDetection } from './hooks/useDuplicateDetection';
import { useUnassignedSSCCs } from './hooks/useUnassignedSSCCs';
import { useFilteredTripSheets } from './hooks/useFilteredTripSheets';
import {
  getQmRecordRequest,
  listAttachmentsRequest,
  listQmRecordsRequest,
  QmAttachment,
  QmCrudStatus,
  QmRecord
} from '../../api/qmCrud';
import { useNavigate } from 'react-router-dom';
import QmDetailDialog from '../qm-coordinator/QmDetailDialog';
import type { QmDetailDialogModel } from '../qm-coordinator/QmDetailDialog';

import {
  ASNToDelete,
  BustleDialogData,
  Manifest,
  SelectedManifest,
  SelectedManifestSSCC,
  SelectedUnassignedSSCC,
  SsccActionsContext,
  SsccActionsPayload
} from './utils/types';
import ReportingLogs from './components/ReportingLogs';
import { HEADER_HEIGHT } from './utils/values';
import { captureError } from '../../common/sentry';

const PAGE_SIZE = 10;
const UNASSIGNED_SSCC_PAGE_SIZE = 20;
const SHOW_FILTERS_STORAGE_KEY = 'adminPanel.showFilters';
const SHOW_UNASSIGNED_SSCCS_STORAGE_KEY = 'adminPanel.showUnassignedSSCCs';
const STATUS_FILTER_MENU_PROPS = {
  PaperProps: {
    sx: {
      '& .MuiMenuItem-root': {
        fontSize: '0.82rem',
        minHeight: 34
      }
    }
  }
};
const STATUS_FILTER_INPUT_LABEL_PROPS = {
  sx: {
    fontSize: '0.8rem',
    '&.MuiInputLabel-shrink': {
      fontSize: '0.72rem'
    }
  }
};

const getStoredBoolean = (key: string, fallback: boolean) => {
  const value = localStorage.getItem(key);

  if (value === null) {
    return fallback;
  }

  return value === 'true';
};

const getManifestContextKey = (tripId: string, legId: string, manifestId: string) =>
  `${tripId}::${legId}::${manifestId}`;

const ADMIN_QM_LOOKUP_STATUSES: QmCrudStatus[] = ['CREATED', 'PENDING'];
const ADMIN_QM_LOOKUP_KEY_SEPARATOR = '\u001f';

const normalizeAdminQmLookupValue = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const splitAdminQmLookupValues = (value: string | null | undefined): string[] =>
  String(value || '')
    .split(/[,\s/]+/)
    .map((part) => normalizeAdminQmLookupValue(part))
    .filter(Boolean);

const appendAdminQmMatch = (map: Map<string, QmRecord[]>, key: string, record: QmRecord) => {
  const normalizedKey = normalizeAdminQmLookupValue(key);
  if (!normalizedKey) return;

  const existing = map.get(normalizedKey) ?? [];
  if (existing.some((item) => item.qmNumber === record.qmNumber)) return;

  map.set(normalizedKey, [...existing, record]);
};

const listAllAdminQmRecordsForStatus = async (status: QmCrudStatus): Promise<QmRecord[]> => {
  const pageSize = 100;
  const firstPage = await listQmRecordsRequest({
    page: 1,
    pageSize,
    filter: {
      status,
      types: ['SITE_INBOUND']
    }
  });
  const totalPages = Math.max(1, Math.ceil(firstPage.totalCount / pageSize));

  if (totalPages === 1) {
    return firstPage.items;
  }

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      listQmRecordsRequest({
        page: index + 2,
        pageSize,
        filter: {
          status,
          types: ['SITE_INBOUND']
        }
      })
    )
  );

  return [firstPage, ...remainingPages].flatMap((page) => page.items);
};

export default function AdminPanel() {
  const user = useCurrentUser();
  const alert = useAlert();
  const alertRef = useRef(alert);
  const navigate = useNavigate();
  const { loading: ssccLoading } = usePhasedModelGate('SSCC');
  const { loading: tripLoading } = usePhasedModelGate('TripSheet');
  const dataLoading = ssccLoading || tripLoading;

  useEffect(() => {
    alertRef.current = alert;
  }, [alert]);

  const [showHistory, setShowHistory] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [unassignedSSCCPage, setUnassignedSSCCPage] = useState(1);
  const [showFilters, setShowFilters] = useState(() => getStoredBoolean(SHOW_FILTERS_STORAGE_KEY, true));
  const [ssccActionsOpen, setSsccActionsOpen] = useState(false);
  const [ssccActionsPayload, setSsccActionsPayload] = useState<SsccActionsPayload | null>(null);

  const [tripSearch, setTripSearch] = useState('');
  const [departureAddressSearch, setDepartureAddressSearch] = useState('');
  const [legDestinationSearch, setLegDestinationSearch] = useState('');
  const [selectedTripStatus, setSelectedTripStatus] = useState('');
  const [selectedLegStatus, setSelectedLegStatus] = useState('');
  const [selectedManifestStatus, setSelectedManifestStatus] = useState('');
  const [selectedSSCCStatus, setSelectedSSCCStatus] = useState('');
  const [selectedASNStatus, setSelectedASNStatus] = useState('');
  const [selectedLegDepartureDate, setSelectedLegDepartureDate] = useState('');
  const [selectedLegArrivalDate, setSelectedLegArrivalDate] = useState('');
  const [manifestSearch, setManifestSearch] = useState('');
  const [manifestTrailerIdSearch, setManifestTrailerIdSearch] = useState('');
  const [ssccSearch, setSsccSearch] = useState('');
  const [ssccNicknameSearch, setSsccNicknameSearch] = useState('');
  const [asnSearch, setAsnSearch] = useState('');
  const [asnPoNumberSearch, setAsnPoNumberSearch] = useState('');

  // Keep typing responsive while worker-backed filters catch up.
  const deferredTripSearch = useDeferredValue(tripSearch);
  const deferredDepartureAddressSearch = useDeferredValue(departureAddressSearch);
  const deferredLegDestinationSearch = useDeferredValue(legDestinationSearch);
  const deferredSelectedTripStatus = useDeferredValue(selectedTripStatus);
  const deferredSelectedLegStatus = useDeferredValue(selectedLegStatus);
  const deferredSelectedManifestStatus = useDeferredValue(selectedManifestStatus);
  const deferredSelectedSSCCStatus = useDeferredValue(selectedSSCCStatus);
  const deferredSelectedASNStatus = useDeferredValue(selectedASNStatus);
  const deferredSelectedLegDepartureDate = useDeferredValue(selectedLegDepartureDate);
  const deferredSelectedLegArrivalDate = useDeferredValue(selectedLegArrivalDate);
  const deferredManifestSearch = useDeferredValue(manifestSearch);
  const deferredManifestTrailerIdSearch = useDeferredValue(manifestTrailerIdSearch);
  const deferredSsccSearch = useDeferredValue(ssccSearch);
  const deferredSsccNicknameSearch = useDeferredValue(ssccNicknameSearch);
  const deferredAsnSearch = useDeferredValue(asnSearch);
  const deferredAsnPoNumberSearch = useDeferredValue(asnPoNumberSearch);

  // Hooks that depend on date range - must be after createdFrom/createdTo state declarations
  const rawTripSheets = useObserveTripSheets({ mode: 'admin-panel' });

  const store = useDatastore();
  const ssccs = useObserveSsccs(0);

  const [hideEmptyManifestTrips, setHideEmptyManifestTrips] = useState(false);
  const [showOnlyManifestDuplicates, setShowOnlyManifestDuplicates] = useState(false);
  const [showOnlySSCCDuplicates, setShowOnlySSCCDuplicates] = useState(false);
  const [showOnlyASNDuplicates] = useState(false);
  const [showUnassignedSSCCs, setShowUnassignedSSCCs] = useState(() =>
    getStoredBoolean(SHOW_UNASSIGNED_SSCCS_STORAGE_KEY, true)
  );

  // 01 Add SSCC Dialog State
  const [openAddSSCCToManifestDialog, setOpenAddSSCCToManifestDialog] = useState(false);
  const [addSSCCToManifestData, setAddSSCCToManifestData] = useState<SelectedManifest | null>(null);
  const [addSSCCToManifestSelectedId, setAddSSCCToManifestSelectedId] = useState<string>('');
  const [addSSCCToManifestWeight, setAddSSCCToManifestWeight] = useState<string>('0');
  const [addSSCCToManifestReceiveManifest, setAddSSCCToManifestReceiveManifest] = useState(true);

  // 02 Mark Completed Manifest Dialog State
  const [openCompleteManifestDialog, setOpenCompleteManifestDialog] = useState(false);
  const [completeManifestData, setCompleteManifestData] = useState<SelectedManifest | null>(null);

  // 03 Delete Manifest Dialog State
  const [openDeleteManifestDialog, setOpenDeleteManifestDialog] = useState(false);
  const [deleteManifestData, setDeleteManifestData] = useState<SelectedManifest | null>(null);

  // 04 Add ASN Dialog State
  const [openAddASNToSSCCDialog, setOpenAddASNToSSCCDialog] = useState(false);
  const [addASNToSSCCData, setAddASNToSSCCData] = useState<SelectedManifestSSCC | null>(null);
  const [addASNToUnassignedSSCCData, setAddASNToUnassignedSSCCData] = useState<SelectedUnassignedSSCC | null>(null);
  const [addASNToSSCCSelectedId, setAddASNToSSCCSelectedId] = useState<string>('');
  const [addASNToSSCCReceiptEvent, setAddASNToSSCCReceiptEvent] = useState<string>('');
  const [addASNToSSCCReceiveManifest, setAddASNToSSCCReceiveManifest] = useState(true);

  // 05 Mark Completed SSCC Dialog State
  const [openCompleteSSCCDialog, setOpenCompleteSSCCDialog] = useState(false);
  const [completeSSCCData, setCompleteSSCCData] = useState<SelectedManifestSSCC | null>(null);
  const [completeUnassignedSSCCData, setCompleteUnassignedSSCCData] = useState<SelectedUnassignedSSCC | null>(null);
  const [completeSSCCWeight, setCompleteSSCCWeight] = useState<string>('0');

  // 06 Delete SSCC Dialog State
  const [openDeleteSSCCDialog, setOpenDeleteSSCCDialog] = useState(false);
  const [deleteSSCCData, setDeleteSSCCData] = useState<SelectedManifestSSCC | null>(null);
  const [openDeleteUnassignedSSCCDialog, setOpenDeleteUnassignedSSCCDialog] = useState(false);
  const [deleteUnassignedSSCCData, setDeleteUnassignedSSCCData] = useState<SelectedUnassignedSSCC | null>(null);

  // 07 Delete ASN Dialog State
  const [openDeleteASNDialog, setOpenDeleteASNDialog] = useState(false);
  const [deleteASNData, setDeleteASNData] = useState<ASNToDelete | null>(null);
  const [deleteASNTransferSSCCId, setDeleteASNTransferSSCCId] = useState('');
  const [deleteASNTransferReceiptedEvent, setDeleteASNTransferReceiptedEvent] = useState('');

  // General Dialog State
  const [openBustleDialog, setOpenBustleDialog] = useState<BustleDialogData | null>(null);
  const [openCoupaDialog, setOpenCoupaDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [adminQmMatchesByLookupKey, setAdminQmMatchesByLookupKey] = useState<Map<string, QmRecord[]>>(new Map());
  const [adminPanelSelectedQm, setAdminPanelSelectedQm] = useState<QmRecord | null>(null);
  const [isAdminPanelQmDetailsLoading, setIsAdminPanelQmDetailsLoading] = useState(false);
  const [adminPanelQmAttachments, setAdminPanelQmAttachments] = useState<QmAttachment[]>([]);
  const [isAdminPanelQmAttachmentsLoading, setIsAdminPanelQmAttachmentsLoading] = useState(false);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const [expandedTrips, setExpandedTrips] = useState<Set<string>>(new Set());

  const openStagedSsccs = useMemo(
    () =>
      ssccs
        .filter((s) => s.status === SsccStatus.open || s.status === SsccStatus.staged)
        .sort((a, b) => dayjs(b.id).valueOf() - dayjs(a.id).valueOf()),
    [ssccs]
  );

  const openStagedSsccById = useMemo(() => new Map(openStagedSsccs.map((sscc) => [sscc.id, sscc])), [openStagedSsccs]);

  const allSsccById = useMemo(() => new Map(ssccs.map((sscc) => [sscc.id, sscc])), [ssccs]);

  const ssccStatusById = useMemo(() => {
    const map: Record<string, string> = {};
    ssccs.forEach((sscc) => {
      map[sscc.id] = sscc.status || '';
    });
    return map;
  }, [ssccs]);

  const [availableASNsForAssignedSSCC, setAvailableASNsForAssignedSSCC] = useState<ReceiptEventPackageProps[]>([]);
  const [availableASNsForUnassignedSSCC, setAvailableASNsForUnassignedSSCC] = useState<ReceiptEventPackageProps[]>([]);

  const availableASNsForAssignedSSCCByAsn = useMemo(
    () => new Map(availableASNsForAssignedSSCC.map((asn) => [asn.asn, asn] as const)),
    [availableASNsForAssignedSSCC]
  );

  const availableASNsForUnassignedSSCCByAsn = useMemo(
    () => new Map(availableASNsForUnassignedSSCC.map((asn) => [asn.asn, asn] as const)),
    [availableASNsForUnassignedSSCC]
  );

  const selectedAssignedASN = useMemo(
    () => availableASNsForAssignedSSCCByAsn.get(addASNToSSCCSelectedId) || null,
    [availableASNsForAssignedSSCCByAsn, addASNToSSCCSelectedId]
  );

  const selectedUnassignedASN = useMemo(
    () => availableASNsForUnassignedSSCCByAsn.get(addASNToSSCCSelectedId) || null,
    [availableASNsForUnassignedSSCCByAsn, addASNToSSCCSelectedId]
  );

  const tripSheets = useMemo(() => {
    const result = [...(rawTripSheets || [])];

    return result.sort((a, b) =>
      String(b.reference ?? '').localeCompare(String(a.reference ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
  }, [rawTripSheets]);

  const mergeManifestWithLatestSscc = useCallback(
    (manifest: ManifestProps): ManifestProps => ({
      ...manifest,
      ssccs: manifest.ssccs.map((sscc) => {
        const latestSscc = allSsccById.get(sscc.ssccId);
        return latestSscc ? { ...sscc, ...latestSscc } : sscc;
      })
    }),
    [allSsccById]
  );

  const tripSheetsById = useMemo(() => new Map(tripSheets.map((ts) => [ts.id, ts])), [tripSheets]);

  useEffect(() => {
    localStorage.setItem(SHOW_FILTERS_STORAGE_KEY, String(showFilters));
    localStorage.setItem(SHOW_UNASSIGNED_SSCCS_STORAGE_KEY, String(showUnassignedSSCCs));
  }, [showFilters, showUnassignedSSCCs]);

  // Offload duplicate detection to a Web Worker - runs once on load, sequential steps
  const {
    manifestOccurrences,
    ssccOccurrences,
    asnOccurrences,
    isCalculatingManifest,
    isCalculatingSSCC,
    isCalculatingASN,
    hasCalculated
  } = useDuplicateDetection(tripSheets, ssccs);

  const manifestDuplicateOccurrencesById = useMemo(() => {
    const map = new Map<string, typeof manifestOccurrences extends Map<string, infer V> ? V : never>();
    manifestOccurrences.forEach((occurrences, manifestId) => {
      if (occurrences.length > 1) {
        map.set(manifestId, occurrences);
      }
    });
    return map;
  }, [manifestOccurrences]);

  const manifestDuplicateIds = useMemo(
    () => new Set(Array.from(manifestDuplicateOccurrencesById.keys())),
    [manifestDuplicateOccurrencesById]
  );

  const ssccDuplicateIds = useMemo(() => {
    const set = new Set<string>();
    ssccOccurrences.forEach((occurrences, ssccId) => {
      if (occurrences.length > 1) {
        set.add(ssccId);
      }
    });
    return set;
  }, [ssccOccurrences]);

  const asnDuplicateKeys = useMemo(() => {
    const set = new Set<string>();
    asnOccurrences.forEach((occurrences, asnKey) => {
      if (occurrences.length > 1) {
        set.add(asnKey);
      }
    });
    return set;
  }, [asnOccurrences]);

  const tripDisplayStatusByContext = useMemo(() => {
    const map = new Map<string, TripLegStatus>();

    tripSheets.forEach((trip) => {
      trip.legs.forEach((leg) => {
        leg.manifests.forEach((manifest) => {
          map.set(
            getManifestContextKey(trip.id, leg.id, manifest.id),
            getTripDisplayStatus({
              manifestStatus: manifest.status,
              legStatus: leg.status
            })
          );
        });
      });
    });

    return map;
  }, [tripSheets]);

  // Compute unassigned SSCCs (SSCCs not in any manifest) - offloaded to a Web Worker
  const unassignedSSCCs = useUnassignedSSCCs({
    openStagedSsccById,
    ssccOccurrences,
    asnOccurrences,
    ssccSearch: deferredSsccSearch,
    selectedSSCCStatus: deferredSelectedSSCCStatus,
    ssccNicknameSearch: deferredSsccNicknameSearch,
    asnSearch: deferredAsnSearch,
    selectedASNStatus: deferredSelectedASNStatus,
    asnPoNumberSearch: deferredAsnPoNumberSearch,
    showOnlyASNDuplicates
  });

  const unassignedSSCCTotalPages = useMemo(
    () => Math.max(1, Math.ceil(unassignedSSCCs.length / UNASSIGNED_SSCC_PAGE_SIZE)),
    [unassignedSSCCs.length]
  );

  const paginatedUnassignedSSCCs = useMemo(() => {
    const startIndex = (unassignedSSCCPage - 1) * UNASSIGNED_SSCC_PAGE_SIZE;
    return unassignedSSCCs.slice(startIndex, startIndex + UNASSIGNED_SSCC_PAGE_SIZE);
  }, [unassignedSSCCPage, unassignedSSCCs]);

  const paginatedUnassignedDuplicateAsnCountById = useMemo(() => {
    const duplicateCountById = new Map<string, number>();

    paginatedUnassignedSSCCs.forEach((sscc) => {
      const duplicateAsnCount = new Set(
        (sscc.receiptedEvents || [])
          .flatMap((event) => event.packages || [])
          .map((pkg) => buildAsnKey(pkg))
          .filter((asnKey): asnKey is string => typeof asnKey === 'string' && asnDuplicateKeys.has(asnKey))
      ).size;

      duplicateCountById.set(sscc.id, duplicateAsnCount);
    });

    return duplicateCountById;
  }, [paginatedUnassignedSSCCs, asnDuplicateKeys]);

  useEffect(() => {
    setUnassignedSSCCPage(1);
  }, [
    showUnassignedSSCCs,
    ssccSearch,
    selectedSSCCStatus,
    ssccNicknameSearch,
    asnSearch,
    selectedASNStatus,
    asnPoNumberSearch
  ]);

  useEffect(() => {
    if (unassignedSSCCPage > unassignedSSCCTotalPages) {
      setUnassignedSSCCPage(unassignedSSCCTotalPages);
    }
  }, [unassignedSSCCPage, unassignedSSCCTotalPages]);

  const eligibleTransferSSCCs = useMemo(() => {
    const assignedSSCCIds = new Set<string>();

    (tripSheets || []).forEach((trip) => {
      trip.legs.forEach((leg) => {
        leg.manifests.forEach((manifest) => {
          manifest.ssccs.forEach((sscc) => {
            if (!sscc?.ssccId) {
              return;
            }

            assignedSSCCIds.add(sscc.ssccId);
          });
        });
      });
    });

    return (openStagedSsccs || []).filter((sscc) => !assignedSSCCIds.has(sscc.id));
  }, [tripSheets, openStagedSsccs]);

  // Filter tripsheets based on search query and date - offloaded to a Web Worker
  const filteredTripSheets = useFilteredTripSheets({
    tripSheets,
    tripSearch: deferredTripSearch,
    departureAddressSearch: deferredDepartureAddressSearch,
    legDestinationSearch: deferredLegDestinationSearch,
    manifestSearch: deferredManifestSearch,
    manifestTrailerIdSearch: deferredManifestTrailerIdSearch,
    asnSearch: deferredAsnSearch,
    asnPoNumberSearch: deferredAsnPoNumberSearch,
    ssccSearch: deferredSsccSearch,
    ssccNicknameSearch: deferredSsccNicknameSearch,
    selectedLegDepartureDate: deferredSelectedLegDepartureDate,
    selectedLegArrivalDate: deferredSelectedLegArrivalDate,
    selectedTripStatus: deferredSelectedTripStatus,
    selectedLegStatus: deferredSelectedLegStatus,
    selectedManifestStatus: deferredSelectedManifestStatus,
    selectedSSCCStatus: deferredSelectedSSCCStatus,
    selectedASNStatus: deferredSelectedASNStatus,
    ssccStatusById,
    hideEmptyManifestTrips,
    showOnlyManifestDuplicates,
    manifestOccurrences,
    showOnlySSCCDuplicates,
    ssccOccurrences,
    showOnlyASNDuplicates,
    asnOccurrences
  });

  const totalPages = useMemo(() => Math.ceil(filteredTripSheets.length / PAGE_SIZE), [filteredTripSheets.length]);

  const paginatedTripSheets = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredTripSheets.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredTripSheets, currentPage]);

  const tripDupeStats = useMemo(() => {
    const stats = new Map<string, { manifestDupeCount: number; ssccDupeCount: number; asnDupeCount: number }>();
    paginatedTripSheets.forEach((trip) => {
      const manifestDupeCount = new Set(
        trip.legs
          .flatMap((leg) => leg.manifests.map((manifest) => manifest.id))
          .filter((manifestId) => manifestDuplicateIds.has(manifestId))
      ).size;

      const ssccDupeCount = new Set(
        trip.legs
          .flatMap((leg) => leg.manifests.flatMap((manifest) => manifest.ssccs?.map((sscc) => sscc.ssccId) || []))
          .filter((ssccId) => ssccDuplicateIds.has(ssccId))
      ).size;

      const asnDupeCount = new Set(
        trip.legs
          .flatMap((leg) =>
            leg.manifests.flatMap((manifest) => {
              const transformedManifest = transformManifest({
                manifest: mergeManifestWithLatestSscc(manifest),
                trip,
                leg
              });

              return transformedManifest.ssccsWithAsns.flatMap((ssccWithAsns) => ssccWithAsns.asns);
            })
          )
          .map((asn) => buildAsnKey(asn))
          .filter((asnKey): asnKey is string => typeof asnKey === 'string')
          .filter((asnKey) => asnDuplicateKeys.has(asnKey))
      ).size;

      stats.set(trip.id, { manifestDupeCount, ssccDupeCount, asnDupeCount });
    });
    return stats;
  }, [paginatedTripSheets, manifestDuplicateIds, ssccDuplicateIds, asnDuplicateKeys, mergeManifestWithLatestSscc]);

  const duplicateTotals = useMemo(
    () => ({
      manifest: manifestDuplicateIds.size,
      sscc: ssccDuplicateIds.size,
      asn: asnDuplicateKeys.size
    }),
    [manifestDuplicateIds, ssccDuplicateIds, asnDuplicateKeys]
  );

  const transformedExpandedManifestsByContext = useMemo(() => {
    const map = new Map<string, Manifest>();

    paginatedTripSheets.forEach((trip) => {
      if (!expandedTrips.has(trip.id)) {
        return;
      }

      trip.legs.forEach((leg) => {
        leg.manifests.forEach((manifest) => {
          map.set(
            getManifestContextKey(trip.id, leg.id, manifest.id),
            transformManifest({ manifest: mergeManifestWithLatestSscc(manifest), trip, leg })
          );
        });
      });
    });

    return map;
  }, [paginatedTripSheets, expandedTrips, mergeManifestWithLatestSscc]);

  const adminQmLookupData = useMemo(() => {
    const displayValues = new Set<string>();
    const searchValues = new Set<string>();

    const addLookupValue = (value: unknown) => {
      const normalizedValue = normalizeAdminQmLookupValue(value);
      if (!normalizedValue) return;

      displayValues.add(normalizedValue);
      searchValues.add(normalizedValue);
    };

    transformedExpandedManifestsByContext.forEach((manifest) => {
      const manifestKey = normalizeAdminQmLookupValue(manifest.id);
      if (manifestKey) searchValues.add(manifestKey);

      manifest.ssccsWithAsns.forEach((ssccWithAsns) => {
        addLookupValue(ssccWithAsns.ssccId);

        ssccWithAsns.asns.forEach((asn) => {
          addLookupValue(asn.asn);
        });
      });
    });

    return {
      displayKey: Array.from(displayValues).sort().join(ADMIN_QM_LOOKUP_KEY_SEPARATOR),
      searchKey: Array.from(searchValues).sort().join(ADMIN_QM_LOOKUP_KEY_SEPARATOR)
    };
  }, [transformedExpandedManifestsByContext]);

  useEffect(() => {
    const searchValues = adminQmLookupData.searchKey
      ? adminQmLookupData.searchKey.split(ADMIN_QM_LOOKUP_KEY_SEPARATOR)
      : [];
    const displayValues = adminQmLookupData.displayKey
      ? adminQmLookupData.displayKey.split(ADMIN_QM_LOOKUP_KEY_SEPARATOR)
      : [];

    if (searchValues.length === 0) {
      setAdminQmMatchesByLookupKey(new Map());
      return;
    }

    let cancelled = false;

    async function loadAdminQmMatches() {
      try {
        const nextMatches = new Map<string, QmRecord[]>();
        const displayValueSet = new Set(displayValues);

        const recordsByStatus = await Promise.all(ADMIN_QM_LOOKUP_STATUSES.map(listAllAdminQmRecordsForStatus));

        recordsByStatus
          .flat()
          .filter((record) => record.type === 'SITE_INBOUND' && ADMIN_QM_LOOKUP_STATUSES.includes(record.status))
          .forEach((record) => {
            const ssccValues = splitAdminQmLookupValues(record.sscc);
            const asnValues = splitAdminQmLookupValues(record.asn);
            const manifestValues = splitAdminQmLookupValues(record.manifestId);
            const recordMatchesExpandedTrip = [...ssccValues, ...asnValues, ...manifestValues].some((value) =>
              searchValues.includes(value)
            );

            if (!recordMatchesExpandedTrip) {
              return;
            }

            [...ssccValues, ...asnValues]
              .filter((value) => displayValueSet.has(value))
              .forEach((value) => appendAdminQmMatch(nextMatches, value, record));
          });

        if (!cancelled) {
          setAdminQmMatchesByLookupKey(nextMatches);
        }
      } catch (error) {
        captureError(error, 'adminPanel-loadQmNumberLabels');
        if (!cancelled) {
          setAdminQmMatchesByLookupKey(new Map());
          alertRef.current('Unable to load QM labels for this admin panel view.', 'error');
        }
      }
    }

    void loadAdminQmMatches();

    return () => {
      cancelled = true;
    };
  }, [adminQmLookupData.displayKey, adminQmLookupData.searchKey]);

  const openAdminPanelQmDetails = useCallback(
    async (record: QmRecord, event?: React.MouseEvent) => {
      event?.stopPropagation();
      setIsAdminPanelQmDetailsLoading(true);
      setIsAdminPanelQmAttachmentsLoading(true);
      setAdminPanelQmAttachments([]);
      setAdminPanelSelectedQm(record);

      try {
        const [detailRecord, attachmentResults] = await Promise.all([
          getQmRecordRequest({ qmId: record.qmId, qmNumber: record.qmNumber }),
          listAttachmentsRequest(record.qmId)
        ]);
        setAdminPanelSelectedQm(detailRecord || record);
        setAdminPanelQmAttachments(attachmentResults || []);
      } catch (error) {
        captureError(error, 'adminPanel-openQmDetails', {
          extra: { qmId: record.qmId, qmNumber: record.qmNumber }
        });
        alert('Unable to load QM details. Showing the available QM summary.', 'error');
      } finally {
        setIsAdminPanelQmDetailsLoading(false);
        setIsAdminPanelQmAttachmentsLoading(false);
      }
    },
    [alert]
  );

  const closeAdminPanelQmDetails = useCallback(() => {
    setAdminPanelSelectedQm(null);
    setIsAdminPanelQmDetailsLoading(false);
    setAdminPanelQmAttachments([]);
    setIsAdminPanelQmAttachmentsLoading(false);
  }, []);

  const adminPanelQmSectionCardSx = {
    p: 1.5,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper'
  } as const;

  const adminPanelQmDetailDialogModel: QmDetailDialogModel = {
    selectedQm: adminPanelSelectedQm,
    isDetailsLoading: isAdminPanelQmDetailsLoading,
    canEdit: false,
    canUploadAttachments: false,
    canReprintLabels: false,
    showFilteredOutNotice: false,
    sectionCardSx: adminPanelQmSectionCardSx,
    attachments: adminPanelQmAttachments,
    isAttachmentsLoading: isAdminPanelQmAttachmentsLoading,
    isUploadingAttachments: false,
    onUploadAttachments: () => undefined,
    onSelectPreview: () => undefined,
    actionComment: '',
    isAddingActionComment: false,
    onActionCommentChange: () => undefined,
    addActionComment: () => undefined,
    onReprintLabels: () => undefined,
    isReprintingLabels: false,
    editStatus: adminPanelSelectedQm?.status || 'CREATED',
    onEditStatusChange: () => undefined,
    editReasonCode: adminPanelSelectedQm?.reasonCode || '',
    onEditReasonCodeChange: () => undefined,
    editSupplierName: adminPanelSelectedQm?.supplierName || '',
    onEditSupplierNameChange: () => undefined,
    editContactName: adminPanelSelectedQm?.contactName || '',
    onEditContactNameChange: () => undefined,
    editItemLocation: adminPanelSelectedQm?.itemLocation || '',
    onEditItemLocationChange: () => undefined,
    editOtherInformation: adminPanelSelectedQm?.otherInformation || '',
    onEditOtherInformationChange: () => undefined,
    editDescription: adminPanelSelectedQm?.description || '',
    onEditDescriptionChange: () => undefined,
    editClosureCode: adminPanelSelectedQm?.closureCode || '',
    onEditClosureCodeChange: () => undefined,
    editClosureNotes: adminPanelSelectedQm?.closureNotes || '',
    onEditClosureNotesChange: () => undefined,
    saveQmStatusEdits: async () => false,
    saveQmDetailEdits: async () => false,
    closureOptions: [],
    reasonOptions: [],
    editReasonOptions: [],
    locationOptions: [],
    getLocationLabel: (itemLocation) => String(itemLocation || '-'),
    getQmTypeLabel: (type, typeLabel) => String(typeLabel || type || '-'),
    statusOptions: ADMIN_QM_LOOKUP_STATUSES,
    formatStatusLabel: (status) =>
      String(status || '-')
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase()),
    onOpenInQmCases: () => {
      if (!adminPanelSelectedQm) return;
      navigate(`/qm-cases?qm=${encodeURIComponent(adminPanelSelectedQm.qmNumber)}`);
    },
    onCloseForNavigation: closeAdminPanelQmDetails
  };

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleUnassignedSSCCPageChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => {
    setUnassignedSSCCPage(value);
  }, []);

  const handleHideEmptyManifestTripsToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setHideEmptyManifestTrips(event.target.checked);
    setCurrentPage(1);
  }, []);

  const handleShowOnlyManifestDuplicatesToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyManifestDuplicates(event.target.checked);
    setCurrentPage(1);
  }, []);

  const handleShowOnlySSCCDuplicatesToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlySSCCDuplicates(event.target.checked);
    setCurrentPage(1);
  }, []);

  const handleTripSearchChange = useCallback((value: string) => {
    setTripSearch(value);
    setCurrentPage(1);
  }, []);

  const handleDepartureAddressSearchChange = useCallback((value: string) => {
    setDepartureAddressSearch(value);
    setCurrentPage(1);
  }, []);

  const handleLegDestinationSearchChange = useCallback((value: string) => {
    setLegDestinationSearch(value);
    setCurrentPage(1);
  }, []);

  const handleTripStatusChange = useCallback((value: string) => {
    setSelectedTripStatus(value);
    setCurrentPage(1);
  }, []);

  const handleLegStatusChange = useCallback((value: string) => {
    setSelectedLegStatus(value);
    setCurrentPage(1);
  }, []);

  const handleSSCCStatusChange = useCallback((value: string) => {
    setSelectedSSCCStatus(value);
    setCurrentPage(1);
  }, []);

  const handleASNStatusChange = useCallback((value: string) => {
    setSelectedASNStatus(value);
    setCurrentPage(1);
  }, []);

  const handleLegDepartureDateChange = useCallback((value: string) => {
    setSelectedLegDepartureDate(value);
    setCurrentPage(1);
  }, []);

  const handleLegArrivalDateChange = useCallback((value: string) => {
    setSelectedLegArrivalDate(value);
    setCurrentPage(1);
  }, []);

  const handleManifestSearchChange = useCallback((value: string) => {
    setManifestSearch(value);
    setCurrentPage(1);
  }, []);

  const handleManifestTrailerIdSearchChange = useCallback((value: string) => {
    setManifestTrailerIdSearch(value);
    setCurrentPage(1);
  }, []);

  const handleAsnSearchChange = useCallback((value: string) => {
    setAsnSearch(value);
    setCurrentPage(1);
  }, []);

  const handleAsnPoNumberSearchChange = useCallback((value: string) => {
    setAsnPoNumberSearch(value);
    setCurrentPage(1);
  }, []);

  const handleSsccSearchChange = useCallback((value: string) => {
    setSsccSearch(value);
    setCurrentPage(1);
  }, []);

  const handleSsccNicknameSearchChange = useCallback((value: string) => {
    setSsccNicknameSearch(value);
    setCurrentPage(1);
  }, []);

  const ssccActionsContext: SsccActionsContext | null = useMemo(() => {
    if (!ssccActionsOpen || !ssccActionsPayload) return null;

    const trip = tripSheetsById.get(ssccActionsPayload.tripId);
    if (!trip) return null;

    const leg = trip.legs.find((l) => l.id === ssccActionsPayload.legId);
    if (!leg) return null;

    const manifest = leg.manifests.find((m) => m.id === ssccActionsPayload.manifestId);
    if (!manifest) return null;

    const manifestContextKey = getManifestContextKey(trip.id, leg.id, manifest.id);
    const tripDisplayStatus =
      tripDisplayStatusByContext.get(manifestContextKey) ??
      getTripDisplayStatus({
        manifestStatus: manifest.status,
        legStatus: leg.status
      });

    const transformedManifest =
      transformedExpandedManifestsByContext.get(manifestContextKey) ??
      transformManifest({ manifest: mergeManifestWithLatestSscc(manifest), trip, leg });
    const ssccEntry = transformedManifest.ssccsWithAsns.find((item) => item.ssccId === ssccActionsPayload.ssccId);

    return { trip, leg, manifest, transformedManifest, ssccEntry, tripDisplayStatus };
  }, [
    ssccActionsOpen,
    ssccActionsPayload,
    tripDisplayStatusByContext,
    transformedExpandedManifestsByContext,
    mergeManifestWithLatestSscc,
    tripSheetsById
  ]);

  // 00
  const handleCloseSsccActions = () => {
    setSsccActionsOpen(false);
    setSsccActionsPayload(null);
  };

  // 01A
  const handleOpenAddSSCCToManifestDialog = async ({
    manifest,
    tripDisplayStatus,
    ssccStatus,
    asnStatus,
    tripId,
    legId
  }: {
    manifest: Manifest;
    tripDisplayStatus: TripLegStatus;
    ssccStatus: string;
    asnStatus: string;
    tripId: string;
    legId: string;
    ssccId?: string;
  }) => {
    setAddSSCCToManifestData({ manifest, tripDisplayStatus, ssccStatus, asnStatus, tripId, legId });
    setOpenAddSSCCToManifestDialog(true);
  };

  // 01B
  const resetAddSSCCToManifestState = () => {
    setOpenAddSSCCToManifestDialog(false);
    setAddSSCCToManifestData(null);
    setAddSSCCToManifestSelectedId('');
    setAddSSCCToManifestWeight('0');
    setAddSSCCToManifestReceiveManifest(true);
  };

  // 01C
  const handleAddSSCCToManifest = async (actionReason: string) => {
    const withReceiveManifestCheck = addSSCCToManifestData?.manifest?.status === ManifestStatus.completed;

    // Original logic for assigned SSCCs (adding to manifest)
    // Validation
    if (!addSSCCToManifestData) {
      alert('No manifest selected', 'error');
      return;
    }

    if (!addSSCCToManifestSelectedId) {
      alert('Please select an SSCC to add', 'error');
      return;
    }

    if (!actionReason.trim()) {
      alert('Reason for adding SSCC is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    const inventoryTransfer = withReceiveManifestCheck && addSSCCToManifestReceiveManifest;

    try {
      setIsProcessing(true);

      const result = await addSSCCToManifest({
        selectedManifest: addSSCCToManifestData,
        selectedSSCCId: addSSCCToManifestSelectedId,
        ssccWeight: addSSCCToManifestWeight,
        actionReason,
        inventoryTransfer,
        receiveManifestOptionShown: withReceiveManifestCheck,
        alert,
        store,
        user
      });

      if (result) {
        alert(
          `SSCC ${addSSCCToManifestSelectedId} successfully added to manifest ${addSSCCToManifestData.manifest.id}`,
          'success'
        );
        // Clean up state in order
        resetAddSSCCToManifestState();

        // Show Bustle dialog
        setOpenBustleDialog({
          message: 'Access Bustle and Cancel Job',
          messageDetail: 'Please access Bustle to cancel any related jobs for this SSCC addition.'
        });
      } else {
        alert('Failed to add SSCC to manifest. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error adding SSCC to manifest:', error);
      captureError(error, 'adminPanel-addSSCCToManifest');
      alert('An unexpected error occurred while adding the SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 02A
  const handleOpenMarkCompletedManifestDialog = ({
    tripDisplayStatus,
    transformedManifest,
    manifestStatus,
    tripId,
    legId
  }: {
    tripDisplayStatus: TripLegStatus;
    transformedManifest: Manifest;
    manifestStatus: ManifestStatus;
    tripId: string;
    legId: string;
  }) => {
    const ssccStatus = mapTripLegStatusToSsccStatus(tripDisplayStatus);

    const asnStatus = mapSsccStatusToAsnStatus({
      ssccStatus,
      manifestStatus
    });

    setCompleteManifestData({
      manifest: transformedManifest,
      tripDisplayStatus,
      ssccStatus,
      asnStatus,
      tripId,
      legId
    });
    setOpenCompleteManifestDialog(true);
  };

  // 02B
  const resetCompleteManifestState = () => {
    setOpenCompleteManifestDialog(false);
    setCompleteManifestData(null);
  };

  // 02C
  const handleMarkAsCompletedManifest = async (actionReason: string) => {
    // Validation
    if (!completeManifestData) {
      alert('No manifest selected to mark as completed', 'error');
      return;
    }

    if (!actionReason.trim()) {
      alert('Reason for completion is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await markManifestCompleted({
        selectedManifest: completeManifestData,
        actionReason,
        alert,
        store,
        user,
        onlySendToCoupa: false
      });

      if (result) {
        // Clean up state in order
        resetCompleteManifestState();
      } else {
        alert('Failed to mark manifest as completed. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error marking manifest as completed:', error);
      captureError(error, 'adminPanel-markManifestCompleted');
      alert('An unexpected error occurred while completing the manifest. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 03A
  const handleOpenDeleteManifestDialog = ({
    tripDisplayStatus,
    manifestStatus,
    transformedManifest,
    tripId,
    legId
  }: {
    tripDisplayStatus: TripLegStatus;
    manifestStatus: ManifestStatus;
    transformedManifest: Manifest;
    tripId: string;
    legId: string;
  }) => {
    const ssccStatus = mapTripLegStatusToSsccStatus(tripDisplayStatus);

    const asnStatus = mapSsccStatusToAsnStatus({
      ssccStatus,
      manifestStatus
    });

    setDeleteManifestData({
      manifest: transformedManifest,
      tripDisplayStatus,
      ssccStatus,
      asnStatus,
      tripId,
      legId
    });
    setOpenDeleteManifestDialog(true);
  };

  // 03B
  const resetDeleteManifestState = () => {
    setOpenDeleteManifestDialog(false);
    setDeleteManifestData(null);
  };

  // 03C
  const handleDeleteManifest = async (actionReason: string) => {
    // Validation
    if (!deleteManifestData) {
      alert('No manifest selected for deletion', 'error');
      return;
    }

    if (!actionReason.trim()) {
      alert('Reason for deletion is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await deleteManifest({
        selectedManifest: deleteManifestData,
        actionReason,
        alert,
        store,
        user
      });

      if (result) {
        alert(`Manifest ${deleteManifestData.manifest.id} has been removed.`, 'success');
        // Clean up state in order
        resetDeleteManifestState();

        deleteManifestData.tripDisplayStatus === TripLegStatus.completed && setOpenCoupaDialog(true);
      } else {
        alert('Failed to delete manifest. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error deleting manifest:', error);
      captureError(error, 'adminPanel-deleteManifest');
      alert('An unexpected error occurred while deleting the manifest. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 04A
  const handleOpenAddASNToSSCCDialog = async () => {
    let resolvedParam: SelectedManifestSSCC | SelectedUnassignedSSCC | string | null = null;

    if (ssccActionsContext) {
      const ssccStatus = mapTripLegStatusToSsccStatus(ssccActionsContext.tripDisplayStatus);
      const asnStatus = mapSsccStatusToAsnStatus({
        ssccStatus,
        manifestStatus: ssccActionsContext.manifest.status
      });

      const ssccId = ssccActionsContext.ssccEntry?.ssccId;
      const sscc = ssccActionsContext.ssccEntry;

      if (!ssccId || !sscc) {
        alert('SSCC information is missing, cannot proceed with marking as completed.', 'error');
        return;
      }

      resolvedParam = {
        manifest: ssccActionsContext.transformedManifest,
        tripDisplayStatus: ssccActionsContext.tripDisplayStatus,
        ssccStatus,
        asnStatus,
        tripId: ssccActionsContext.trip.id,
        legId: ssccActionsContext.leg.id,
        ssccId,
        sscc
      };
    } else {
      resolvedParam = ssccActionsPayload?.ssccId || '';
    }

    const asnMap = new Map<string, ReceiptEventPackageProps>();
    openStagedSsccs.forEach((sscc) => {
      sscc.receiptedEvents?.forEach((event) => {
        event.packages?.forEach((pkg) => {
          const isAvailableAsn = (pkg.status || '').toString().toLowerCase() === AsnStatus.available.toLowerCase();
          if (pkg.asn && !asnMap.has(pkg.asn) && !isAvailableAsn) {
            asnMap.set(pkg.asn, pkg);
          }
        });
      });
    });
    const computedASNs = Array.from(asnMap.values()).sort((a, b) => (a.asn || '').localeCompare(b.asn || ''));

    if (typeof resolvedParam === 'object') {
      const { manifest, tripDisplayStatus, ssccStatus, asnStatus, tripId, legId, ssccId, sscc } = resolvedParam;

      setAddASNToSSCCData({ manifest, tripDisplayStatus, ssccStatus, asnStatus, tripId, legId, ssccId, sscc });

      const currentSsccAsns = new Set(
        (sscc?.sscc?.receiptedEvents || [])
          .flatMap((event) => event.packages || [])
          .map((pkg) => pkg.asn)
          .filter((asn): asn is string => Boolean(asn))
      );
      setAvailableASNsForAssignedSSCC(computedASNs.filter((asn) => !asn.asn || !currentSsccAsns.has(asn.asn)));
    } else if (typeof resolvedParam === 'string') {
      const sscc = openStagedSsccById.get(resolvedParam);

      setAddASNToUnassignedSSCCData({
        ssccId: resolvedParam,
        sscc,
        ssccStatus: sscc?.status?.toString() || SsccStatus.staged
      });

      const currentSsccAsns = new Set(
        (sscc?.receiptedEvents || [])
          .flatMap((event) => event.packages || [])
          .map((pkg) => pkg.asn)
          .filter((asn): asn is string => Boolean(asn))
      );
      setAvailableASNsForUnassignedSSCC(computedASNs.filter((asn) => !asn.asn || !currentSsccAsns.has(asn.asn)));
    }

    setOpenAddASNToSSCCDialog(true);
  };

  // 04B
  const resetAddASNToSSCCState = () => {
    setOpenAddASNToSSCCDialog(false);
    setAddASNToSSCCData(null);
    setAddASNToUnassignedSSCCData(null);
    setAddASNToSSCCSelectedId('');
    setAddASNToSSCCReceiptEvent('');
    setAddASNToSSCCReceiveManifest(true);
  };

  // 04C-A
  const handleAddASNToSSCC = async ({
    selectedManifestSSCC,
    tripSheets,
    withReceiveManifestCheck,
    asn,
    ssccId,
    actionReason,
    tripId,
    legId,
    manifestId,
    asnStatus,
    receiptedEventIndex
  }: {
    selectedManifestSSCC: SelectedManifestSSCC | null;
    tripSheets: TripSheetProps[];
    asn: ReceiptEventPackageProps | null;
    withReceiveManifestCheck: boolean;
    ssccId: string;
    actionReason: string;
    tripId?: string;
    legId?: string;
    manifestId?: string;
    asnStatus: string;
    receiptedEventIndex?: number;
  }) => {
    // Validation
    if (!asn) {
      alert('No ASN selected', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    const inventoryTransfer = withReceiveManifestCheck && addASNToSSCCReceiveManifest;

    try {
      setIsProcessing(true);

      const result = await addASNToSSCC({
        selectedManifestSSCC,
        tripSheets,
        asn,
        ssccId,
        tripId,
        manifestId,
        legId,
        actionReason,
        receiptedEventIndex,
        asnStatus,
        alert,
        store,
        user,
        inventoryTransfer,
        receiveManifestOptionShown: withReceiveManifestCheck
      });

      if (result) {
        alert(`ASN ${addASNToSSCCSelectedId} successfully added to SSCC ${ssccId}`, 'success');

        // Remove ASN from previous place

        // Clean up state in order
        resetAddASNToSSCCState();
      } else {
        alert('Failed to add ASN to SSCC. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error adding ASN to SSCC:', error);
      captureError(error, 'adminPanel-addASNToSSCC');
      alert('An unexpected error occurred while adding the ASN to SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 04C-B
  const handleAddASNToUnassignedSSCC = async ({
    asn,
    ssccId,
    actionReason,
    asnStatus,
    receiptedEventIndex,
    doNotCancelLoading
  }: {
    asn: ReceiptEventPackageProps | null;
    ssccId: string;
    actionReason: string;
    asnStatus: string;
    receiptedEventIndex?: number;
    doNotCancelLoading?: boolean;
  }): Promise<boolean> => {
    // Validation
    if (!asn) {
      alert('No ASN selected', 'error');
      return false;
    }

    if (isProcessing) {
      return false;
    }

    try {
      setIsProcessing(true);

      const result = await addASNToUnassignedSSCC({
        asn,
        ssccId,
        actionReason,
        receiptedEventIndex,
        asnStatus,
        alert,
        store,
        user
      });

      if (result) {
        alert(`ASN ${addASNToSSCCSelectedId} successfully added to SSCC ${ssccId}`, 'success');
        // Clean up state in order

        resetAddASNToSSCCState();
        return true;
      } else {
        alert('Failed to add ASN to SSCC. Please verify the data and try again.', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error adding ASN to SSCC:', error);
      captureError(error, 'adminPanel-addASNToUnassignedSSCC');
      alert('An unexpected error occurred while adding the ASN to SSCC. Please try again.', 'error');
      return false;
    } finally {
      !doNotCancelLoading && setIsProcessing(false);
    }
  };

  // 05A
  const handleOpenMarkCompletedSSCCDialog = () => {
    let resolvedData: SelectedManifestSSCC | SelectedUnassignedSSCC | string | null = null;

    if (ssccActionsContext) {
      const ssccStatus = mapTripLegStatusToSsccStatus(ssccActionsContext.tripDisplayStatus);
      const asnStatus = mapSsccStatusToAsnStatus({
        ssccStatus,
        manifestStatus: ssccActionsContext.manifest.status
      });

      const ssccId = ssccActionsContext.ssccEntry?.ssccId;
      const sscc = ssccActionsContext.ssccEntry;

      if (!ssccId || !sscc) {
        alert('SSCC information is missing, cannot proceed with marking as completed.', 'error');
        return;
      }

      resolvedData = {
        manifest: ssccActionsContext.transformedManifest,
        tripDisplayStatus: ssccActionsContext.tripDisplayStatus,
        ssccStatus,
        asnStatus,
        tripId: ssccActionsContext.trip.id,
        legId: ssccActionsContext.leg.id,
        ssccId,
        sscc
      };
    } else {
      resolvedData = ssccActionsPayload?.ssccId || '';
    }

    if (typeof resolvedData === 'string') {
      const sscc = openStagedSsccById.get(resolvedData);

      setCompleteSSCCData(null);
      setCompleteUnassignedSSCCData({
        ssccId: resolvedData,
        sscc,
        ssccStatus: sscc?.status || SsccStatus.staged
      });
      setCompleteSSCCWeight(sscc?.weight ?? '0');
      setOpenCompleteSSCCDialog(true);
      return;
    }

    if (typeof resolvedData === 'object') {
      if ('manifest' in resolvedData) {
        setCompleteUnassignedSSCCData(null);
        setCompleteSSCCData(resolvedData as SelectedManifestSSCC);
        setCompleteSSCCWeight((resolvedData as SelectedManifestSSCC).sscc?.sscc?.weight ?? '0');
      } else {
        setCompleteSSCCData(null);
        setCompleteUnassignedSSCCData(resolvedData as SelectedUnassignedSSCC);
        setCompleteSSCCWeight((resolvedData as SelectedUnassignedSSCC).sscc?.weight ?? '0');
      }
      setOpenCompleteSSCCDialog(true);
      return;
    }

    alert('Invalid data for marking SSCC as completed.', 'error');
  };

  // 05B
  const resetCompleteSSCCState = () => {
    setOpenCompleteSSCCDialog(false);
    setCompleteSSCCData(null);
    setCompleteUnassignedSSCCData(null);
    setCompleteSSCCWeight('0');
  };

  // 05C-A
  const handleMarkAsCompletedSSCC = async (actionReason: string) => {
    // Validation
    if (!completeSSCCData) {
      alert('No SSCC selected to mark as completed', 'error');
      return;
    }

    if (!actionReason.trim()) {
      alert('Reason for completion is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await markSSCCCompleted({
        selectedManifestSSCC: completeSSCCData,
        actionReason,
        alert,
        store,
        user,
        tripReference: completeSSCCData?.tripId,
        ssccWeight: completeSSCCWeight,
        onlySendToCoupa: false
      });

      if (result) {
        // Clean up state in order
        resetCompleteSSCCState();
      } else {
        alert('Failed to mark SSCC as completed. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error marking SSCC as completed:', error);
      captureError(error, 'adminPanel-markSSCCCompleted');
      alert('An unexpected error occurred while completing the SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 05C-B
  const handleMarkAsCompletedUnassignedSSCC = async (actionReason: string) => {
    // Validation
    if (!completeUnassignedSSCCData) {
      alert('No SSCC selected to mark as completed', 'error');
      return;
    }

    if (!actionReason.trim()) {
      alert('Reason for completion is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await markUnassignedSSCCCompleted({
        sscc: completeUnassignedSSCCData.sscc,
        actionReason,
        alert,
        store,
        user,
        ssccWeight: completeSSCCWeight
      });

      if (result) {
        // Clean up state in order
        resetCompleteSSCCState();
      } else {
        alert('Failed to mark SSCC as completed. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error marking SSCC as completed:', error);
      captureError(error, 'adminPanel-markUnassignedSSCCCompleted');
      alert('An unexpected error occurred while completing the SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 06A
  const handleOpenDeleteSSCCDialog = () => {
    if (!ssccActionsContext) {
      alert('This action requires the SSCC to be assigned to a manifest', 'info');
      return;
    }
    const ssccStatus = mapTripLegStatusToSsccStatus(ssccActionsContext.tripDisplayStatus);
    const asnStatus = mapSsccStatusToAsnStatus({
      ssccStatus,
      manifestStatus: ssccActionsContext.manifest.status
    });

    const ssccId = ssccActionsContext.ssccEntry?.ssccId;
    const sscc = ssccActionsContext.ssccEntry;

    if (!ssccId || !sscc) {
      alert('SSCC information is missing, cannot proceed with marking as completed.', 'error');
      return;
    }

    setDeleteSSCCData({
      manifest: ssccActionsContext.transformedManifest,
      tripDisplayStatus: ssccActionsContext.tripDisplayStatus,
      ssccStatus,
      asnStatus,
      tripId: ssccActionsContext.trip.id,
      legId: ssccActionsContext.leg.id,
      ssccId,
      sscc
    });

    setOpenDeleteSSCCDialog(true);
  };

  // 06B
  const resetDeleteSSCCState = () => {
    setOpenDeleteSSCCDialog(false);
    setDeleteSSCCData(null);
  };

  // 06C
  const handleDeleteSSCC = async (actionReason: string) => {
    // Validation
    if (!deleteSSCCData) {
      alert('No SSCC selected for deletion', 'error');
      return;
    }

    if (!actionReason.trim()) {
      alert('Reason for deletion is required', 'error');
      return;
    }

    if (!deleteSSCCData.ssccId) {
      alert('SSCC identifier is missing. Please close and try again.', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await deleteSSCC({
        tripId: deleteSSCCData.tripId,
        legId: deleteSSCCData.legId,
        ssccId: deleteSSCCData.ssccId,
        actionReason,
        alert,
        store,
        user
      });

      if (result) {
        alert('SSCC has been removed.', 'success');
        // Clean up state in order
        resetDeleteSSCCState();

        handleCloseSsccActions();

        deleteSSCCData.tripDisplayStatus === TripLegStatus.completed && setOpenCoupaDialog(true);

        setOpenBustleDialog({
          message: 'Access Bustle, remove job from manifest and reinstate',
          messageDetail:
            'Please access Bustle to remove any related jobs for this SSCC from the manifest and reinstate the SSCC.'
        });
      } else {
        alert('Failed to delete SSCC. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error deleting SSCC:', error);
      captureError(error, 'adminPanel-deleteSSCC');
      alert('An unexpected error occurred while deleting the SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 06D
  const handleOpenDeleteUnassignedSSCCDialog = () => {
    const ssccId = ssccActionsPayload?.ssccId || '';
    const sscc = openStagedSsccById.get(ssccId);

    if (!ssccId || !sscc) {
      alert('Unassigned SSCC information is missing.', 'error');
      return;
    }

    setDeleteUnassignedSSCCData({
      ssccId,
      sscc,
      ssccStatus: sscc.status || SsccStatus.staged
    });
    setOpenDeleteUnassignedSSCCDialog(true);
  };

  const resetDeleteUnassignedSSCCState = () => {
    setOpenDeleteUnassignedSSCCDialog(false);
    setDeleteUnassignedSSCCData(null);
  };

  // 06E
  const handleDeleteUnassignedSSCC = async (actionReason: string, makeAsnAvailable: boolean) => {
    if (!deleteUnassignedSSCCData) {
      alert('No unassigned SSCC selected for cancellation', 'error');
      return;
    }

    if (!actionReason.trim()) {
      alert('Reason for cancellation is required', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      const result = await deleteUnassignedSSCC({
        ssccId: deleteUnassignedSSCCData.ssccId,
        actionReason: actionReason.trim(),
        makeAsnAvailable,
        alert,
        store,
        user
      });

      if (result) {
        alert(
          `Unassigned SSCC ${deleteUnassignedSSCCData.ssccId} has been cancelled.${makeAsnAvailable ? ' Mark ASN available in Coupa: Yes.' : ''}`,
          'success'
        );
        resetDeleteUnassignedSSCCState();
        handleCloseSsccActions();
      } else {
        alert('Failed to cancel unassigned SSCC. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error deleting unassigned SSCC:', error);
      captureError(error, 'adminPanel-deleteUnassignedSSCC');
      alert('An unexpected error occurred while cancelling the unassigned SSCC. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 07A
  const handleOpenDeleteASNDialog = (asn: ReceiptEventPackageProps) => {
    const asnKey = buildAsnKey(asn);
    const asnDuplicateInfo = asnKey ? (asnOccurrences.get(asnKey) ?? []) : [];
    const isDuplicate = asnDuplicateInfo.length > 1;
    const priorityValue = asn.priority || '';
    const normalizedPriority = priorityValue.toUpperCase();
    const priorityShipping = normalizedPriority.includes('P1') || normalizedPriority.includes('P2');

    const manifestPackages = ssccActionsContext?.ssccEntry?.sscc?.packages || [];

    const normalizedAsnValue = (asn.asn || '').trim().toLowerCase();
    const normalizedPoValue = (asn.po || '').trim().toLowerCase();

    const matchingManifestPackages = manifestPackages.filter((pkg) => {
      const pkgAsn = (pkg.asn || '').trim().toLowerCase();
      const pkgPo = (pkg.poNumber || '').trim().toLowerCase();
      return pkgAsn === normalizedAsnValue && pkgPo === normalizedPoValue;
    });

    const resolvedTripAsnUniqueId = matchingManifestPackages[0]?.id || null;

    setDeleteASNData({
      asn,
      asnUniqueId: asn.id || '',
      tripAsnUniqueId: resolvedTripAsnUniqueId,
      asnStatus: asn.status || 'Unknown Status',
      priority: asn.priority,
      isDuplicate,
      priorityShipping,
      tripDisplayStatus: ssccActionsContext?.tripDisplayStatus,
      tripId: ssccActionsContext?.trip.id || null,
      legId: ssccActionsContext?.leg.id || null,
      ssccId: ssccActionsContext?.ssccEntry?.ssccId || ssccActionsPayload?.ssccId || ''
    });
    setDeleteASNTransferSSCCId('');
    setDeleteASNTransferReceiptedEvent('');
    setOpenDeleteASNDialog(true);
  };

  // 07B
  const resetDeleteASNState = () => {
    setOpenDeleteASNDialog(false);
    setDeleteASNData(null);
    setDeleteASNTransferSSCCId('');
    setDeleteASNTransferReceiptedEvent('');
  };

  // 07C
  const handleDeleteASN = async (actionReason: string) => {
    // Validation
    if (!deleteASNData) {
      alert('No ASN selected for deletion', 'error');
      return;
    }

    if (!actionReason.trim()) {
      alert('Reason for deletion is required', 'error');
      return;
    }

    if (!deleteASNData.asnUniqueId || !deleteASNData.ssccId) {
      alert('Missing required ASN data. Please close and try again.', 'error');
      return;
    }

    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);

      if (!deleteASNData.isDuplicate) {
        const isUnassignedSSCC = !deleteASNData.tripId || !deleteASNData.legId;
        if (!isUnassignedSSCC && !deleteASNData.tripAsnUniqueId) {
          alert('Trip ASN package identifier is missing', 'error');
          return;
        }

        const selectedTransferSSCC = openStagedSsccById.get(deleteASNTransferSSCCId);
        const transferReceiptedEventIndex = selectedTransferSSCC?.receiptedEvents?.findIndex(
          (event, index) => `${index + 1}. ${event.name} - ${event.site}` === deleteASNTransferReceiptedEvent
        );

        // Save to another sscc
        const transferResult = await handleAddASNToUnassignedSSCC({
          asn: deleteASNData.asn,
          ssccId: deleteASNTransferSSCCId,
          actionReason,
          asnStatus: selectedTransferSSCC?.status || SsccStatus.staged,
          receiptedEventIndex:
            transferReceiptedEventIndex !== undefined && transferReceiptedEventIndex >= 0
              ? transferReceiptedEventIndex
              : undefined,
          doNotCancelLoading: true
        });

        if (!transferResult) {
          return;
        }
      }

      const result = await deleteASN({
        asnId: deleteASNData.asn.asn || '',
        asnUniqueId: deleteASNData.asnUniqueId,
        tripAsnUniqueId: deleteASNData.tripAsnUniqueId || null,
        asnPoNumber: deleteASNData.asn.po || null,
        isDuplicate: deleteASNData.isDuplicate === true,
        tripId: deleteASNData.tripId,
        legId: deleteASNData.legId,
        ssccId: deleteASNData.ssccId,
        priorityShipping: deleteASNData.priorityShipping,
        actionReason,
        alert,
        store,
        user
      });

      if (result) {
        alert(`ASN ${deleteASNData.asn.asn} has been removed.`, 'success');
        // Clean up state in order
        resetDeleteASNState();

        deleteASNData.asnStatus === AsnStatus.available && setOpenCoupaDialog(true);
      } else {
        alert('Failed to delete ASN. Please verify the data and try again.', 'error');
      }
    } catch (error) {
      console.error('Error deleting ASN:', error);
      captureError(error, 'adminPanel-deleteASN');
      alert('An unexpected error occurred while deleting the ASN. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (showHistory) {
    return <ReportingLogs onClose={() => setShowHistory(false)} />;
  }

  return (
    <Stack
      spacing={0}
      sx={{
        mt: `${HEADER_HEIGHT}px`,
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
      {/* Filter Section */}
      <Box
        sx={{
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 8,
          px: 3,
          pt: 3,
          pb: 1.5
        }}>
        <Card
          sx={{
            backgroundColor: 'background.paper',
            backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(250,250,252,0.95) 100%)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
            border: '1px solid rgba(15, 23, 42, 0.1)',
            borderRadius: 3,
            overflow: 'hidden'
          }}>
          <Box
            sx={{
              height: '3px',
              background: 'linear-gradient(90deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
              borderRadius: '3px 3px 0 0'
            }}
          />
          <CardContent sx={{ p: 3, pb: 2.5 }}>
            <Stack spacing={3}>
              {/* Filter Header Section */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack
                  width={'100%'}
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  spacing={2}>
                  {/* Left: Icon, Title, Clear Button */}
                  <Stack direction="row" spacing={1.5} alignItems="center" flex={0}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}>
                      <SearchIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', letterSpacing: '-0.3px' }}>
                      Filters
                    </Typography>

                    {(tripSearch ||
                      departureAddressSearch ||
                      legDestinationSearch ||
                      selectedTripStatus ||
                      selectedLegStatus ||
                      manifestSearch ||
                      selectedManifestStatus ||
                      manifestTrailerIdSearch ||
                      asnSearch ||
                      selectedASNStatus ||
                      asnPoNumberSearch ||
                      ssccSearch ||
                      selectedSSCCStatus ||
                      ssccNicknameSearch ||
                      selectedLegDepartureDate ||
                      selectedLegArrivalDate) && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={dataLoading}
                        onClick={() => {
                          setTripSearch('');
                          setDepartureAddressSearch('');
                          setLegDestinationSearch('');
                          setSelectedTripStatus('');
                          setSelectedLegStatus('');
                          setManifestSearch('');
                          setSelectedManifestStatus('');
                          setManifestTrailerIdSearch('');
                          setAsnSearch('');
                          setSelectedASNStatus('');
                          setAsnPoNumberSearch('');
                          setSsccSearch('');
                          setSelectedSSCCStatus('');
                          setSsccNicknameSearch('');
                          setSelectedLegDepartureDate('');
                          setSelectedLegArrivalDate('');
                          setCurrentPage(1);
                        }}
                        sx={{
                          whiteSpace: 'nowrap',
                          fontSize: '0.80rem',
                          textTransform: 'none',
                          fontWeight: 600,
                          ml: 1,
                          borderColor: 'rgba(25, 118, 210, 0.3)',
                          color: '#1976d2',
                          '&:hover': {
                            borderColor: '#1976d2',
                            backgroundColor: 'rgba(25, 118, 210, 0.06)'
                          }
                        }}>
                        Clear All
                      </Button>
                    )}
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowFilters((prev) => !prev)}
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 18,
                            transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        />
                      }
                      sx={{
                        whiteSpace: 'nowrap',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        color: '#1976d2',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.06)'
                        }
                      }}>
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Stack>

                  {/* Right: Toggle Switches */}
                  <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end" flex={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={hideEmptyManifestTrips}
                          onChange={handleHideEmptyManifestTripsToggle}
                          disabled={dataLoading}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: 'text.secondary', fontSize: '0.80rem' }}>
                          Hide Empty
                        </Typography>
                      }
                    />
                    <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showOnlyManifestDuplicates}
                          onChange={handleShowOnlyManifestDuplicatesToggle}
                          disabled={dataLoading}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: 'text.secondary', fontSize: '0.80rem' }}>
                          Manifest Dupes
                        </Typography>
                      }
                    />
                    <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showOnlySSCCDuplicates}
                          onChange={handleShowOnlySSCCDuplicatesToggle}
                          disabled={dataLoading}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: 'text.secondary', fontSize: '0.80rem' }}>
                          SSCC Dupes
                        </Typography>
                      }
                    />
                    {/* <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showOnlyASNDuplicates}
                          onChange={handleShowOnlyASNDuplicatesToggle}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{ color: 'text.secondary', fontSize: '0.80rem' }}>
                          ASN Dupes
                        </Typography>
                      }
                    /> */}
                    <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowHistory(true);
                      }}
                      sx={{
                        whiteSpace: 'nowrap',
                        fontSize: '0.80rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: 'rgba(25, 118, 210, 0.3)',
                        color: '#1976d2',
                        '&:hover': {
                          borderColor: '#1976d2',
                          backgroundColor: 'rgba(25, 118, 210, 0.06)'
                        }
                      }}>
                      History
                    </Button>
                  </Stack>
                </Stack>
              </Stack>

              {/* Search Fields Section */}
              {showFilters && (
                <>
                  {/* Divider */}
                  <Box sx={{ height: '1px', backgroundColor: 'rgba(15, 23, 42, 0.08)', borderRadius: '1px' }} />

                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={2} gap={2} alignItems="flex-start" width="100%">
                      {/* Trip Column */}
                      <Stack direction="column" spacing={1.2} alignItems="stretch" flex={1} sx={{ order: 1 }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            ml: 0.5
                          }}>
                          Trip
                        </Typography>
                        <Stack direction="column" spacing={1} alignItems="stretch">
                          <TextField
                            fullWidth
                            placeholder="Trip Number..."
                            value={tripSearch}
                            onChange={(e) => handleTripSearchChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <LocalShippingIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />
                              ),
                              endAdornment: tripSearch ? (
                                <IconButton size="small" onClick={() => handleTripSearchChange('')} sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: tripSearch ? 'rgba(25, 118, 210, 0.08)' : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: tripSearch ? 'rgba(25, 118, 210, 0.12)' : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            placeholder="Origin..."
                            value={departureAddressSearch}
                            onChange={(e) => handleDepartureAddressSearchChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputProps={{
                              startAdornment: <LocationOnIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: departureAddressSearch ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleDepartureAddressSearchChange('')}
                                  sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: departureAddressSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: departureAddressSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            placeholder="Destination..."
                            value={legDestinationSearch}
                            onChange={(e) => handleLegDestinationSearchChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputProps={{
                              startAdornment: <LocationOnIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: legDestinationSearch ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleLegDestinationSearchChange('')}
                                  sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: legDestinationSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: legDestinationSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                        </Stack>
                      </Stack>

                      {/* Leg Column */}
                      <Stack direction="column" spacing={1.2} alignItems="stretch" flex={1} sx={{ order: 2 }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            ml: 0.5
                          }}>
                          Leg
                        </Typography>
                        <Stack direction="column" spacing={1} alignItems="stretch">
                          <TextField
                            fullWidth
                            type="date"
                            label="Departure"
                            value={selectedLegDepartureDate}
                            onChange={(e) => handleLegDepartureDateChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputLabelProps={{
                              shrink: true
                            }}
                            InputProps={{
                              endAdornment: selectedLegDepartureDate ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    aria-label="Clear leg departure date"
                                    onClick={() => handleLegDepartureDateChange('')}>
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : null
                            }}
                            sx={{
                              minWidth: 'auto',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: selectedLegDepartureDate
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: selectedLegDepartureDate
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            type="date"
                            label="Arrival"
                            value={selectedLegArrivalDate}
                            onChange={(e) => handleLegArrivalDateChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputLabelProps={{
                              shrink: true
                            }}
                            InputProps={{
                              endAdornment: selectedLegArrivalDate ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    aria-label="Clear leg arrival date"
                                    onClick={() => handleLegArrivalDateChange('')}>
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : null
                            }}
                            sx={{
                              minWidth: 'auto',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: selectedLegArrivalDate
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: selectedLegArrivalDate
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            select
                            label="Leg Status"
                            value={selectedLegStatus}
                            onChange={(e) => handleLegStatusChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputLabelProps={STATUS_FILTER_INPUT_LABEL_PROPS}
                            SelectProps={{ MenuProps: STATUS_FILTER_MENU_PROPS }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: selectedLegStatus
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: selectedLegStatus
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}>
                            <MenuItem value="">All Statuses</MenuItem>
                            {Object.values(TripLegStatus).map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </Stack>

                      {/* Manifest Column */}
                      <Stack direction="column" spacing={1.2} alignItems="stretch" flex={1} sx={{ order: 3 }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            ml: 0.5
                          }}>
                          Manifest
                        </Typography>
                        <Stack direction="column" spacing={1} alignItems="stretch">
                          <TextField
                            fullWidth
                            placeholder="Manifest Number..."
                            value={manifestSearch}
                            onChange={(e) => handleManifestSearchChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputProps={{
                              startAdornment: <InventoryIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: manifestSearch ? (
                                <IconButton size="small" onClick={() => handleManifestSearchChange('')} sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: manifestSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: manifestSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            placeholder="Trailer ID..."
                            value={manifestTrailerIdSearch}
                            onChange={(e) => handleManifestTrailerIdSearchChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputProps={{
                              startAdornment: <StorageIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: manifestTrailerIdSearch ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleManifestTrailerIdSearchChange('')}
                                  sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: manifestTrailerIdSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: manifestTrailerIdSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            select
                            label="Manifest Status"
                            value={selectedTripStatus}
                            onChange={(e) => handleTripStatusChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputLabelProps={STATUS_FILTER_INPUT_LABEL_PROPS}
                            SelectProps={{ MenuProps: STATUS_FILTER_MENU_PROPS }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: selectedTripStatus
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: selectedTripStatus
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}>
                            <MenuItem value="">All Statuses</MenuItem>
                            {Object.values(TripLegStatus).map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </Stack>

                      {/* SSCC Column */}
                      <Stack direction="column" spacing={1.2} alignItems="stretch" flex={1} sx={{ order: 4 }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            ml: 0.5
                          }}>
                          SSCC
                        </Typography>
                        <Stack direction="column" spacing={1} alignItems="stretch">
                          <TextField
                            fullWidth
                            placeholder="SSCC..."
                            value={ssccSearch}
                            onChange={(e) => handleSsccSearchChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputProps={{
                              startAdornment: <AssignmentIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: ssccSearch ? (
                                <IconButton size="small" onClick={() => handleSsccSearchChange('')} sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: ssccSearch ? 'rgba(25, 118, 210, 0.08)' : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: ssccSearch ? 'rgba(25, 118, 210, 0.12)' : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            placeholder="Nickname..."
                            value={ssccNicknameSearch}
                            onChange={(e) => handleSsccNicknameSearchChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputProps={{
                              startAdornment: <LabelIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: ssccNicknameSearch ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleSsccNicknameSearchChange('')}
                                  sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: ssccNicknameSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: ssccNicknameSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />

                          <TextField
                            fullWidth
                            select
                            label="SSCC Status"
                            value={selectedSSCCStatus}
                            onChange={(e) => handleSSCCStatusChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputLabelProps={STATUS_FILTER_INPUT_LABEL_PROPS}
                            SelectProps={{ MenuProps: STATUS_FILTER_MENU_PROPS }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: selectedSSCCStatus
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: selectedSSCCStatus
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}>
                            <MenuItem value="">All Statuses</MenuItem>
                            {Object.values(SsccStatus).map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </Stack>

                      {/* ASN Column */}
                      <Stack direction="column" spacing={1.2} alignItems="stretch" flex={1} sx={{ order: 5 }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            ml: 0.5
                          }}>
                          ASN
                        </Typography>
                        <Stack direction="column" spacing={1} alignItems="stretch">
                          <TextField
                            fullWidth
                            placeholder="ASN..."
                            value={asnSearch}
                            onChange={(e) => handleAsnSearchChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputProps={{
                              startAdornment: <DescriptionIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />,
                              endAdornment: asnSearch ? (
                                <IconButton size="small" onClick={() => handleAsnSearchChange('')} sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: asnSearch ? 'rgba(25, 118, 210, 0.08)' : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: asnSearch ? 'rgba(25, 118, 210, 0.12)' : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />

                          <TextField
                            fullWidth
                            placeholder="PO Number..."
                            value={asnPoNumberSearch}
                            onChange={(e) => handleAsnPoNumberSearchChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <ShoppingCartIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 19 }} />
                              ),
                              endAdornment: asnPoNumberSearch ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleAsnPoNumberSearchChange('')}
                                  sx={{ p: 0.5 }}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: asnPoNumberSearch
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: asnPoNumberSearch
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}
                          />
                          <TextField
                            fullWidth
                            select
                            label="ASN Status"
                            value={selectedASNStatus}
                            onChange={(e) => handleASNStatusChange(e.target.value)}
                            disabled={dataLoading}
                            size="small"
                            InputLabelProps={STATUS_FILTER_INPUT_LABEL_PROPS}
                            SelectProps={{ MenuProps: STATUS_FILTER_MENU_PROPS }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: selectedASNStatus
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(250, 250, 252, 0.6)',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: selectedASNStatus
                                    ? 'rgba(25, 118, 210, 0.12)'
                                    : 'rgba(250, 250, 252, 0.9)'
                                }
                              }
                            }}>
                            <MenuItem value="">All Statuses</MenuItem>
                            {Object.values(AsnStatus).map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {dataLoading ? (
        <Box sx={{ px: 3, pt: 3 }}>
          <DataLoadingNotice
            label="Admin data"
            message="SSCC and trip sheet data are still loading in the background. Admin actions will be available once sync completes."
          />
        </Box>
      ) : (
        <>
          {/* Trip Sheets Header - Sticky */}
          {filteredTripSheets.length > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                position: 'sticky',
                top: 0,
                zIndex: 7,
                px: 3,
                py: 1.5,
                backgroundColor: 'rgba(255,255,255,0.95)',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                backdropFilter: 'blur(4px)'
              }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                      sx={{
                        p: 0.75,
                        borderRadius: 1.2,
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                      <LocalShippingIcon sx={{ color: '#1976d2', fontSize: 18 }} />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: 'text.primary', letterSpacing: '-0.3px' }}>
                      Trip Sheets
                    </Typography>
                    <Chip
                      label={filteredTripSheets.length}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.80rem',
                        fontWeight: 700,
                        backgroundColor: 'rgba(25, 118, 210, 0.12)',
                        color: '#1976d2'
                      }}
                    />
                  </Box>
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Management Tools
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                          • Mark as Completed: Manually complete manifests when system/process issues prevent normal
                          completion
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                          • Delete Manifest: Permanently remove duplicate or erroneous manifests
                        </Typography>
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{ mt: 1, fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>
                          All actions are logged in the audit trail and require a reason
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="right">
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={2.5} alignItems="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                      Manifest Dupes
                    </Typography>
                    {isCalculatingManifest ? (
                      <CircularProgress size={18} thickness={5} sx={{ color: '#c62828' }} />
                    ) : (
                      <Chip
                        label={duplicateTotals.manifest}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.80rem',
                          fontWeight: 600,
                          backgroundColor: 'rgba(211, 47, 47, 0.1)',
                          color: '#c62828'
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                      SSCC Dupes
                    </Typography>
                    {isCalculatingSSCC ? (
                      <CircularProgress size={18} thickness={5} sx={{ color: '#c62828' }} />
                    ) : (
                      <Chip
                        label={duplicateTotals.sscc}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.80rem',
                          fontWeight: 600,
                          backgroundColor: 'rgba(211, 47, 47, 0.1)',
                          color: '#c62828'
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 23, 42, 0.1)' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                      ASN Dupes
                    </Typography>
                    {isCalculatingASN ? (
                      <CircularProgress size={18} thickness={5} sx={{ color: '#c62828' }} />
                    ) : (
                      <Chip
                        label={duplicateTotals.asn}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.80rem',
                          fontWeight: 600,
                          backgroundColor: 'rgba(211, 47, 47, 0.1)',
                          color: '#c62828'
                        }}
                      />
                    )}
                  </Box>
                </Stack>
              </Stack>
            </Box>
          )}

          <Box
            ref={contentScrollRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 3,
              pt: 1,
              pb: 2,
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(15, 23, 42, 0.2)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.3)'
                }
              }
            }}>
            <Box sx={{ maxWidth: '85%', margin: '0 auto', width: '100%' }}>
              {/* Unassigned SSCCs Section */}
              {currentPage === 1 && (
                <Card
                  sx={{
                    mb: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 3,
                    overflow: 'hidden'
                  }}>
                  <Box
                    sx={{
                      height: '3px',
                      background: 'linear-gradient(90deg, #ed6c02 0%, #e65100 100%)'
                    }}
                  />
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: 'rgba(237, 108, 2, 0.08)',
                            display: 'flex'
                          }}>
                          <InventoryIcon sx={{ fontSize: 24, color: '#ed6c02' }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary' }}>
                            Unassigned SSCCs
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            SSCCs not assigned to any trip, leg, or manifest
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={showUnassignedSSCCs}
                              onChange={(e) => setShowUnassignedSSCCs(e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ color: 'text.secondary', fontSize: '0.80rem' }}>
                              Unassigned SSCCs
                            </Typography>
                          }
                          sx={{ mr: 0.5 }}
                        />
                        <Chip
                          label={`${unassignedSSCCs.length} ${unassignedSSCCs.length === 1 ? 'SSCC' : 'SSCCs'}`}
                          size="medium"
                          sx={{
                            height: 32,
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            backgroundColor: 'rgba(237, 108, 2, 0.12)',
                            color: '#ed6c02'
                          }}
                        />
                      </Stack>

                      {showUnassignedSSCCs && (
                        <>
                          <Divider sx={{ my: 1 }} />

                          {unassignedSSCCs.length > 0 ? (
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                                gap: 2,
                                maxHeight: '400px',
                                overflowY: 'auto',
                                pr: 1,
                                '&::-webkit-scrollbar': {
                                  width: '6px'
                                },
                                '&::-webkit-scrollbar-track': {
                                  backgroundColor: 'rgba(0,0,0,0.05)',
                                  borderRadius: '3px'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                  backgroundColor: 'rgba(0,0,0,0.2)',
                                  borderRadius: '3px',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.3)'
                                  }
                                }
                              }}>
                              {paginatedUnassignedSSCCs.map((sscc) => (
                                <UnassignedSsccCard
                                  key={sscc.id}
                                  sscc={sscc}
                                  duplicateAsnCount={paginatedUnassignedDuplicateAsnCountById.get(sscc.id) ?? 0}
                                  onClick={() => {
                                    setSsccActionsPayload({
                                      tripId: '',
                                      legId: '',
                                      manifestId: '',
                                      ssccId: sscc.id,
                                      sscc: allSsccById.get(sscc.id)
                                    });
                                    setSsccActionsOpen(true);
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                py: 2,
                                px: 2,
                                borderRadius: 1,
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                border: '1px solid rgba(0,0,0,0.08)'
                              }}>
                              <Typography variant="body2" color="text.secondary" fontStyle="italic" fontSize="0.85rem">
                                No unassigned SSCCs match the current filters.
                              </Typography>
                            </Box>
                          )}

                          {unassignedSSCCs.length > UNASSIGNED_SSCC_PAGE_SIZE && (
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                Showing {(unassignedSSCCPage - 1) * UNASSIGNED_SSCC_PAGE_SIZE + 1}–
                                {Math.min(unassignedSSCCPage * UNASSIGNED_SSCC_PAGE_SIZE, unassignedSSCCs.length)} of{' '}
                                {unassignedSSCCs.length} SSCCs
                              </Typography>
                              <Pagination
                                count={unassignedSSCCTotalPages}
                                page={unassignedSSCCPage}
                                onChange={handleUnassignedSSCCPageChange}
                                color="primary"
                                size="medium"
                                siblingCount={2}
                                boundaryCount={2}
                              />
                            </Stack>
                          )}
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {tripSheets.length === 0 && !tripSearch ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 10,
                    px: 3,
                    borderRadius: 2
                  }}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                      border: '1px dashed rgba(0,0,0,0.12)',
                      mb: 3,
                      display: 'inline-block'
                    }}>
                    <CircularProgress sx={{ color: '#1976d2' }} size={48} />
                  </Box>
                  <Typography variant="h5" color="text.primary" fontWeight={700} sx={{ mt: 2 }}>
                    Loading Trip Sheets
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Syncing data from DataStore
                  </Typography>
                </Box>
              ) : filteredTripSheets.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 10,
                    px: 3,
                    borderRadius: 2
                  }}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                      border: '1px dashed rgba(0,0,0,0.12)',
                      mb: 3,
                      display: 'inline-block'
                    }}>
                    <LocalShippingIcon sx={{ fontSize: 56, color: 'rgba(0,0,0,0.2)' }} />
                  </Box>
                  <Typography variant="h5" color="text.primary" fontWeight={700} sx={{ mt: 2 }}>
                    No Trip Sheets Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Try adjusting your search or filter criteria
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      Unassigned SSCCs
                    </Typography>
                    <Chip
                      size="small"
                      label={showUnassignedSSCCs ? 'Visible' : 'Hidden'}
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        backgroundColor: showUnassignedSSCCs ? 'rgba(46, 125, 50, 0.12)' : 'rgba(0,0,0,0.08)',
                        color: showUnassignedSSCCs ? '#2e7d32' : 'text.secondary'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {unassignedSSCCs.length} matching
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {paginatedTripSheets.map((trip: TripSheetProps) => {
                    const { manifestDupeCount, ssccDupeCount, asnDupeCount } = tripDupeStats.get(trip.id) ?? {
                      manifestDupeCount: 0,
                      ssccDupeCount: 0,
                      asnDupeCount: 0
                    };

                    return (
                      <Accordion
                        key={trip.id}
                        expanded={expandedTrips.has(trip.id)}
                        TransitionProps={{ unmountOnExit: true }}
                        onChange={(_, isExpanded) =>
                          setExpandedTrips((prev) => {
                            const next = new Set(prev);
                            isExpanded ? next.add(trip.id) : next.delete(trip.id);
                            return next;
                          })
                        }
                        sx={{
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          border: '1px solid rgba(0,0,0,0.08)',
                          borderRadius: '12px !important',
                          backgroundColor: '#ffffff',
                          '&:before': { display: 'none' },
                          mb: 2,
                          position: 'relative',
                          zIndex: 0,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                            borderColor: 'rgba(25, 118, 210, 0.2)'
                          }
                        }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            padding: '16px 20px',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.02)'
                            }
                          }}>
                          <Stack
                            direction="row"
                            spacing={2.5}
                            alignItems="flex-start"
                            sx={{
                              width: '100%',
                              pr: 2,
                              py: 1
                            }}>
                            {/* Icon */}
                            <Box
                              sx={{
                                p: 1.75,
                                borderRadius: 2,
                                backgroundColor:
                                  'linear-gradient(135deg, rgba(25, 118, 210, 0.12) 0%, rgba(25, 118, 210, 0.04) 100%)',
                                border: '1px solid rgba(25, 118, 210, 0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 56
                              }}>
                              <LocalShippingIcon sx={{ color: '#1976d2', fontSize: 26, fontWeight: 500 }} />
                            </Box>

                            {/* Trip Info */}
                            <Box sx={{ flex: 1 }}>
                              <Stack spacing={1}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 700,
                                      letterSpacing: '0.3px',
                                      color: 'text.primary',
                                      fontSize: '1rem'
                                    }}>
                                    Trip #{trip.reference || trip.id}
                                  </Typography>
                                </Stack>

                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: 'text.primary',
                                      fontSize: '0.9rem'
                                    }}>
                                    {trip.carrier?.businessName || 'Unknown Carrier'}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '0.8rem'
                                    }}>
                                    •
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '0.85rem'
                                    }}>
                                    {trip.createdAt ? dayjs(trip.createdAt).format(dateFormat) : 'N/A'}
                                  </Typography>
                                </Stack>

                                {/* Duplicate Stats - Horizontal Below Date */}
                                {hasCalculated && (
                                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                                    {manifestDupeCount ? (
                                      <Box
                                        sx={{
                                          minWidth: '165px',
                                          px: 2,
                                          py: 1.25,
                                          borderRadius: 2,
                                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                          border: '1.5px solid rgba(211, 47, 47, 0.2)',
                                          boxShadow: '0 2px 8px rgba(211, 47, 47, 0.1)'
                                        }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <InventoryIcon sx={{ fontSize: 20, color: '#c62828' }} />
                                          <Stack spacing={0.25}>
                                            <Typography
                                              sx={{
                                                fontWeight: 800,
                                                fontSize: '1.1rem',
                                                lineHeight: 1,
                                                color: '#c62828',
                                                letterSpacing: '-0.5px'
                                              }}>
                                              {manifestDupeCount}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                color: '#b71c1c',
                                                lineHeight: 1,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px'
                                              }}>
                                              Manifest Dupes
                                            </Typography>
                                          </Stack>
                                        </Stack>
                                      </Box>
                                    ) : null}
                                    {ssccDupeCount ? (
                                      <Box
                                        sx={{
                                          minWidth: '165px',
                                          px: 2,
                                          py: 1.25,
                                          borderRadius: 2,
                                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                          border: '1.5px solid rgba(211, 47, 47, 0.2)',
                                          boxShadow: '0 2px 8px rgba(211, 47, 47, 0.1)'
                                        }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <AssignmentIcon sx={{ fontSize: 20, color: '#c62828' }} />
                                          <Stack spacing={0.25}>
                                            <Typography
                                              sx={{
                                                fontWeight: 800,
                                                fontSize: '1.1rem',
                                                lineHeight: 1,
                                                color: '#c62828',
                                                letterSpacing: '-0.5px'
                                              }}>
                                              {ssccDupeCount}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                color: '#b71c1c',
                                                lineHeight: 1,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px'
                                              }}>
                                              SSCC Dupes
                                            </Typography>
                                          </Stack>
                                        </Stack>
                                      </Box>
                                    ) : null}
                                    {asnDupeCount ? (
                                      <Box
                                        sx={{
                                          minWidth: '165px',
                                          px: 2,
                                          py: 1.25,
                                          borderRadius: 2,
                                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                          border: '1.5px solid rgba(211, 47, 47, 0.2)',
                                          boxShadow: '0 2px 8px rgba(211, 47, 47, 0.1)'
                                        }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <DescriptionIcon sx={{ fontSize: 20, color: '#c62828' }} />
                                          <Stack spacing={0.25}>
                                            <Typography
                                              sx={{
                                                fontWeight: 800,
                                                fontSize: '1.1rem',
                                                lineHeight: 1,
                                                color: '#c62828',
                                                letterSpacing: '-0.5px'
                                              }}>
                                              {asnDupeCount}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                color: '#b71c1c',
                                                lineHeight: 1,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px'
                                              }}>
                                              ASN Dupes
                                            </Typography>
                                          </Stack>
                                        </Stack>
                                      </Box>
                                    ) : null}
                                  </Stack>
                                )}
                              </Stack>
                            </Box>

                            <Box
                              sx={{
                                minWidth: 480,
                                maxWidth: 560,
                                pl: 3,
                                pr: 1,
                                borderLeft: '2px solid rgba(25, 118, 210, 0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                              }}>
                              <Stack spacing={1.25}>
                                {trip.legs.map((leg, legIndex) => (
                                  <Box
                                    key={leg.id + legIndex}
                                    sx={{
                                      width: '100%'
                                    }}>
                                    <Box
                                      sx={{
                                        px: 2.5,
                                        py: 1.25,
                                        my: 0.75,
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        borderRadius: 2,
                                        border: '1px solid rgba(25, 118, 210, 0.12)',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                          borderColor: 'rgba(25, 118, 210, 0.2)'
                                        }
                                      }}>
                                      <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Typography
                                          sx={{
                                            fontSize: '0.75rem',
                                            fontWeight: 700,
                                            color: '#1976d2',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            minWidth: 'fit-content'
                                          }}>
                                          Leg {legIndex + 1}
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(0,0,0,0.3)', fontWeight: 500 }}>|</Typography>
                                        <Typography
                                          sx={{
                                            fontSize: '0.85rem',
                                            fontWeight: 600,
                                            color: 'text.primary'
                                          }}>
                                          {leg.origin?.businessName || 'Unknown'}
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(25, 118, 210, 0.6)', fontSize: '0.9rem' }}>
                                          →
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: '0.85rem',
                                            fontWeight: 600,
                                            color: 'text.primary'
                                          }}>
                                          {leg.destination?.businessName || 'Unknown'}
                                        </Typography>
                                      </Stack>
                                    </Box>

                                    <Stack
                                      direction="row"
                                      spacing={0.75}
                                      flexWrap="wrap"
                                      sx={{ gap: 0.75, pl: 0.5, mt: 1 }}>
                                      {leg.manifests.length === 0 ? (
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: '0.8rem', pl: 1, fontStyle: 'italic' }}
                                          color="text.secondary">
                                          No manifests
                                        </Typography>
                                      ) : (
                                        leg.manifests.map((manifest, manifestIndex) => {
                                          const isDuplicate = manifestDuplicateIds.has(manifest.id);
                                          const manifestContextKey = getManifestContextKey(
                                            trip.id,
                                            leg.id,
                                            manifest.id
                                          );
                                          const tripDisplayStatus =
                                            tripDisplayStatusByContext.get(manifestContextKey) ??
                                            getTripDisplayStatus({
                                              manifestStatus: manifest.status,
                                              legStatus: leg.status
                                            });

                                          return (
                                            <Box
                                              key={`${legIndex}-${manifest.id}-${manifestIndex}`}
                                              sx={{
                                                px: 2,
                                                py: 1.5,
                                                minWidth: 140,
                                                borderRadius: 2,
                                                border: isDuplicate
                                                  ? '2px solid rgba(211, 47, 47, 0.35)'
                                                  : '1px solid rgba(0,0,0,0.1)',
                                                backgroundColor: isDuplicate
                                                  ? 'rgba(211, 47, 47, 0.06)'
                                                  : tripDisplayStatus === TripLegStatus.completed ||
                                                      tripDisplayStatus === TripLegStatus.arrived
                                                    ? '#ffffff'
                                                    : 'rgba(158, 158, 158, 0.06)',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                                              }}>
                                              <Stack spacing={1} alignItems="center">
                                                {/* Manifest ID */}
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.875rem',
                                                    lineHeight: 1.2,
                                                    color: isDuplicate ? '#c62828' : 'text.primary',
                                                    textAlign: 'center'
                                                  }}>
                                                  {manifest.id}
                                                </Typography>

                                                {/* Status Badge */}
                                                <Chip
                                                  label={tripDisplayStatus}
                                                  size="small"
                                                  sx={{
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.3px',
                                                    backgroundColor:
                                                      tripDisplayStatus === TripLegStatus.completed ||
                                                      tripDisplayStatus === TripLegStatus.arrived
                                                        ? 'rgba(76, 175, 80, 0.15)'
                                                        : 'rgba(158, 158, 158, 0.15)',
                                                    color:
                                                      tripDisplayStatus === TripLegStatus.completed ||
                                                      tripDisplayStatus === TripLegStatus.arrived
                                                        ? '#388e3c'
                                                        : '#616161',
                                                    border: `1px solid ${
                                                      tripDisplayStatus === TripLegStatus.completed ||
                                                      tripDisplayStatus === TripLegStatus.arrived
                                                        ? 'rgba(76, 175, 80, 0.3)'
                                                        : 'rgba(158, 158, 158, 0.3)'
                                                    }`,
                                                    '& .MuiChip-label': {
                                                      px: 1
                                                    }
                                                  }}
                                                />
                                              </Stack>
                                            </Box>
                                          );
                                        })
                                      )}
                                    </Stack>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            px: 3,
                            py: 2.5,
                            backgroundColor: 'rgba(0, 0, 0, 0.01)',
                            borderTop: '1px solid rgba(0,0,0,0.08)'
                          }}>
                          {trip.legs.reduce((sum, leg) => sum + leg.manifests.length, 0) === 0 ? (
                            <Box
                              sx={{
                                textAlign: 'center',
                                py: 5,
                                px: 3,
                                borderRadius: 2,
                                border: '1px dashed rgba(0,0,0,0.12)'
                              }}>
                              <LocalShippingIcon sx={{ fontSize: 48, color: 'rgba(0,0,0,0.26)', mb: 2 }} />
                              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
                                No Legs
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                This trip doesn't have any legs associated with it
                              </Typography>
                            </Box>
                          ) : (
                            <Box>
                              {trip.legs.map((leg, legIndex) => (
                                <Box key={`${trip.id}-${leg.id}-${legIndex}`}>
                                  {legIndex > 0 && (
                                    <Divider
                                      sx={{
                                        my: 4,
                                        borderColor: 'rgba(0,0,0,0.12)'
                                      }}
                                    />
                                  )}
                                  <Box
                                    sx={{
                                      mb: 3,
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                      border: '1px solid rgba(0,0,0,0.06)',
                                      borderRadius: '8px !important',
                                      backgroundColor: 'rgba(25, 118, 210, 0.02)'
                                    }}>
                                    <Box
                                      sx={{
                                        mb: 2,
                                        px: 3,
                                        py: 1.5,
                                        backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                        borderRadius: 2,
                                        border: '1px solid rgba(25, 118, 210, 0.15)',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                      }}>
                                      <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="center"
                                        justifyContent="space-between">
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                          <Typography
                                            sx={{
                                              fontSize: '0.8rem',
                                              fontWeight: 700,
                                              color: '#1976d2',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.5px'
                                            }}>
                                            Leg {legIndex + 1}
                                          </Typography>
                                          <Typography sx={{ color: 'rgba(0,0,0,0.3)', fontWeight: 500 }}>|</Typography>
                                          <Typography
                                            sx={{
                                              fontSize: '0.875rem',
                                              fontWeight: 600,
                                              color: 'text.primary'
                                            }}>
                                            {leg.origin?.businessName || 'Unknown'}
                                          </Typography>
                                          <Typography sx={{ color: 'rgba(25, 118, 210, 0.6)', fontSize: '0.9rem' }}>
                                            →
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: '0.875rem',
                                              fontWeight: 600,
                                              color: 'text.primary'
                                            }}>
                                            {leg.destination?.businessName || 'Unknown'}
                                          </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                          {leg.departureDate && (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                              <Typography
                                                sx={{
                                                  fontSize: '0.7rem',
                                                  fontWeight: 600,
                                                  color: 'rgba(25, 118, 210, 0.7)',
                                                  textTransform: 'uppercase',
                                                  letterSpacing: '0.3px'
                                                }}>
                                                Departure:
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: '0.75rem',
                                                  fontWeight: 700,
                                                  color: '#1976d2'
                                                }}>
                                                {dayjs(leg.departureDate).format(dateFormat)}
                                              </Typography>
                                            </Stack>
                                          )}
                                          {leg.arrivalDate && (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                              <Typography
                                                sx={{
                                                  fontSize: '0.7rem',
                                                  fontWeight: 600,
                                                  color: 'rgba(76, 175, 80, 0.7)',
                                                  textTransform: 'uppercase',
                                                  letterSpacing: '0.3px'
                                                }}>
                                                Arrival:
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: '0.75rem',
                                                  fontWeight: 700,
                                                  color: '#388e3c'
                                                }}>
                                                {dayjs(leg.arrivalDate).format(dateFormat)}
                                              </Typography>
                                            </Stack>
                                          )}
                                        </Stack>
                                      </Stack>
                                    </Box>

                                    {leg.manifests.length === 0 ? (
                                      <Box
                                        sx={{
                                          py: 1.5,
                                          px: 2,
                                          mx: 2,
                                          mb: 2,
                                          borderRadius: 1,
                                          backgroundColor: 'rgba(0,0,0,0.02)',
                                          border: '1px solid rgba(0,0,0,0.08)'
                                        }}>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          fontStyle="italic"
                                          fontSize="0.85rem">
                                          No manifests for this leg
                                        </Typography>
                                      </Box>
                                    ) : (
                                      leg.manifests.map((manifest, manifestIndex) => {
                                        const manifestContextKey = getManifestContextKey(trip.id, leg.id, manifest.id);
                                        const transformedManifest =
                                          transformedExpandedManifestsByContext.get(manifestContextKey) ??
                                          transformManifest({
                                            manifest: mergeManifestWithLatestSscc(manifest),
                                            trip,
                                            leg
                                          });
                                        const manifestDuplicateOccurrences =
                                          manifestDuplicateOccurrencesById.get(manifest.id) ?? [];
                                        const isDuplicateManifest = manifestDuplicateOccurrences.length > 1;

                                        const tripDisplayStatus =
                                          tripDisplayStatusByContext.get(manifestContextKey) ??
                                          getTripDisplayStatus({
                                            manifestStatus: manifest.status,
                                            legStatus: leg.status
                                          });

                                        const canManageManifestActions = [
                                          TripLegStatus.arrived,
                                          TripLegStatus.completed
                                        ].includes(tripDisplayStatus);

                                        return (
                                          <Box key={`${legIndex}-${manifest.id}-${manifestIndex}`} sx={{ px: 3 }}>
                                            <Box
                                              sx={{
                                                mb: 3,
                                                borderRadius: 2,
                                                backgroundColor: 'background.default',
                                                border: isDuplicateManifest
                                                  ? '2px solid #d32f2f'
                                                  : '1px solid rgba(0,0,0,0.12)',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                              }}>
                                              {/* Duplicate Warning */}
                                              {isDuplicateManifest && (
                                                <Box
                                                  sx={{
                                                    backgroundColor: 'rgba(211, 47, 47, 0.06)',
                                                    borderBottom: '1px solid rgba(211, 47, 47, 0.2)',
                                                    px: 2.5,
                                                    py: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5
                                                  }}>
                                                  <ErrorOutlineIcon
                                                    sx={{
                                                      fontSize: 18,
                                                      color: '#c62828'
                                                    }}
                                                  />
                                                  <Stack spacing={1}>
                                                    <Typography
                                                      sx={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 700,
                                                        color: '#c62828',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                      }}>
                                                      Duplicate Detected • {manifestDuplicateOccurrences.length}{' '}
                                                      occurrences
                                                    </Typography>
                                                    <Stack
                                                      direction="row"
                                                      spacing={0.75}
                                                      alignItems="center"
                                                      flexWrap="wrap">
                                                      {manifestDuplicateOccurrences.map((info, manifestDupeIndex) => (
                                                        <Chip
                                                          key={`${info.tripId}-${info.legIndex}-${manifestDupeIndex}`}
                                                          label={`Trip ${info.tripId || 'N/A'} • Leg ${
                                                            Number.isInteger(info.legIndex) ? info.legIndex + 1 : 'N/A'
                                                          }`}
                                                          size="small"
                                                          sx={{
                                                            height: 18,
                                                            fontSize: '0.65rem',
                                                            fontWeight: 600,
                                                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                                            color: '#b71c1c',
                                                            border: '1px solid rgba(211, 47, 47, 0.25)',
                                                            '& .MuiChip-label': {
                                                              px: 1
                                                            }
                                                          }}
                                                        />
                                                      ))}
                                                    </Stack>
                                                  </Stack>
                                                </Box>
                                              )}

                                              {/* Manifest Header */}
                                              <Box
                                                sx={{
                                                  px: 3,
                                                  py: 2.5,
                                                  borderLeft: '1px solid #42a5f5',
                                                  borderRight: '1px solid #42a5f5',
                                                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                                                  position: 'relative',
                                                  borderTopLeftRadius: 8,
                                                  borderTopRightRadius: 8,
                                                  overflow: 'hidden'
                                                }}>
                                                <Box
                                                  sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: 6,
                                                    background: '#1976d2',
                                                    borderTopLeftRadius: 8,
                                                    borderTopRightRadius: 8
                                                  }}
                                                />
                                                <Stack spacing={2} position="relative" zIndex={1}>
                                                  <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="center">
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                      <Box
                                                        sx={{
                                                          width: 40,
                                                          height: 40,
                                                          borderRadius: '50%',
                                                          backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center'
                                                        }}>
                                                        <InventoryIcon
                                                          sx={{
                                                            fontSize: 20,
                                                            color: '#1976d2'
                                                          }}
                                                        />
                                                      </Box>
                                                      <Box>
                                                        <Typography
                                                          variant="h6"
                                                          sx={{
                                                            fontWeight: 700,
                                                            fontSize: '1.1rem',
                                                            letterSpacing: '-0.3px',
                                                            color: 'text.primary'
                                                          }}>
                                                          {manifest.id}
                                                        </Typography>
                                                        <Typography
                                                          variant="caption"
                                                          sx={{
                                                            color: 'text.secondary',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500
                                                          }}>
                                                          Trailer: {manifest.trailerId || 'N/A'}
                                                        </Typography>
                                                      </Box>
                                                    </Stack>
                                                    <Chip
                                                      label={tripDisplayStatus}
                                                      size="small"
                                                      sx={{
                                                        height: 26,
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        textTransform: 'capitalize',
                                                        backgroundColor:
                                                          tripDisplayStatus === TripLegStatus.completed ||
                                                          tripDisplayStatus === TripLegStatus.arrived
                                                            ? 'rgba(76, 175, 80, 0.15)'
                                                            : 'rgba(158, 158, 158, 0.15)',
                                                        color:
                                                          tripDisplayStatus === TripLegStatus.completed ||
                                                          tripDisplayStatus === TripLegStatus.arrived
                                                            ? '#388e3c'
                                                            : '#616161',
                                                        border: `1px solid ${
                                                          tripDisplayStatus === TripLegStatus.completed ||
                                                          tripDisplayStatus === TripLegStatus.arrived
                                                            ? 'rgba(76, 175, 80, 0.3)'
                                                            : 'rgba(158, 158, 158, 0.3)'
                                                        }`
                                                      }}
                                                    />
                                                  </Stack>
                                                </Stack>
                                              </Box>

                                              {/* Manifest Content (unchanged) */}
                                              <Box
                                                sx={{
                                                  p: 3,
                                                  backgroundColor: '#f5f7fa',
                                                  borderLeft: '1px solid #42a5f5',
                                                  borderRight: '1px solid #42a5f5',
                                                  borderBottom: '1px solid #42a5f5',
                                                  borderBottomLeftRadius: 8,
                                                  borderBottomRightRadius: 8
                                                }}>
                                                {/* SSCCs & ASNs (paired per SSCC) */}
                                                <Box>
                                                  <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    sx={{ mb: 1.5 }}>
                                                    <InventoryIcon
                                                      sx={{
                                                        fontSize: 16,
                                                        color: 'text.secondary'
                                                      }}
                                                    />
                                                    <Typography variant="body2" fontWeight={600} color="text.primary">
                                                      SSCCs & ASNs
                                                    </Typography>
                                                    <Chip
                                                      label={transformedManifest.ssccsWithAsns.length}
                                                      size="small"
                                                      sx={{
                                                        height: 18,
                                                        fontSize: '0.7rem',
                                                        backgroundColor: 'rgba(0,0,0,0.06)',
                                                        color: 'text.secondary',
                                                        fontWeight: 600
                                                      }}
                                                    />
                                                  </Stack>

                                                  <Stack spacing={1}>
                                                    {transformedManifest.ssccsWithAsns.map(
                                                      (ssccWithAsns, ssccIndex) => {
                                                        const ssccDuplicateInfo =
                                                          ssccOccurrences.get(ssccWithAsns.ssccId) ?? [];
                                                        const isSsccDuplicate = ssccDuplicateInfo?.length > 1;
                                                        const ssccQmMatches =
                                                          adminQmMatchesByLookupKey.get(
                                                            normalizeAdminQmLookupValue(ssccWithAsns.ssccId)
                                                          ) ?? [];

                                                        return (
                                                          <Box
                                                            key={`${ssccWithAsns.ssccId}-${ssccIndex}`}
                                                            onClick={() => {
                                                              setSsccActionsPayload({
                                                                tripId: trip.id,
                                                                legId: leg.id,
                                                                manifestId: manifest.id,
                                                                ssccId: ssccWithAsns.ssccId,
                                                                sscc: allSsccById.get(ssccWithAsns.ssccId)
                                                              });
                                                              setSsccActionsOpen(true);
                                                            }}
                                                            sx={{
                                                              mb: 3,
                                                              borderRadius: 2,
                                                              backgroundColor: 'background.default',
                                                              border: isSsccDuplicate
                                                                ? '2px solid #d32f2f'
                                                                : '1px solid rgba(0,0,0,0.12)',
                                                              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                                              cursor: 'pointer',
                                                              transition: 'box-shadow 0.2s ease',
                                                              '&:hover': {
                                                                boxShadow: '0 3px 10px rgba(0,0,0,0.12)'
                                                              }
                                                            }}>
                                                            {isSsccDuplicate && (
                                                              <Box
                                                                sx={{
                                                                  backgroundColor: 'rgba(211, 47, 47, 0.06)',
                                                                  borderBottom: '1px solid rgba(211, 47, 47, 0.2)',
                                                                  px: 2.5,
                                                                  py: 1.5,
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  gap: 1.5
                                                                }}>
                                                                <ErrorOutlineIcon
                                                                  sx={{
                                                                    fontSize: 18,
                                                                    color: '#c62828'
                                                                  }}
                                                                />
                                                                <Stack spacing={1}>
                                                                  <Typography
                                                                    sx={{
                                                                      fontSize: '0.75rem',
                                                                      fontWeight: 700,
                                                                      color: '#c62828',
                                                                      textTransform: 'uppercase',
                                                                      letterSpacing: '0.5px'
                                                                    }}>
                                                                    Duplicate Detected • {ssccDuplicateInfo.length}{' '}
                                                                    occurrences
                                                                  </Typography>
                                                                  <Stack
                                                                    direction="row"
                                                                    spacing={0.75}
                                                                    alignItems="center"
                                                                    flexWrap="wrap">
                                                                    {ssccDuplicateInfo.map(
                                                                      (info, ssccDuplicateInfoIndex) => (
                                                                        <Chip
                                                                          key={ssccDuplicateInfoIndex}
                                                                          label={`Trip ${info.tripId || 'N/A'} • Leg ${
                                                                            Number.isInteger(info.legIndex)
                                                                              ? (info.legIndex ?? 0) + 1
                                                                              : 'N/A'
                                                                          } • Manifest ${info.manifestId || 'N/A'}`}
                                                                          size="small"
                                                                          sx={{
                                                                            height: 18,
                                                                            fontSize: '0.65rem',
                                                                            fontWeight: 600,
                                                                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                                                            color: '#b71c1c',
                                                                            border: '1px solid rgba(211, 47, 47, 0.25)',
                                                                            '& .MuiChip-label': {
                                                                              px: 1
                                                                            }
                                                                          }}
                                                                        />
                                                                      )
                                                                    )}
                                                                  </Stack>
                                                                </Stack>
                                                              </Box>
                                                            )}
                                                            <Box
                                                              sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                                p: 1.5,
                                                                backgroundColor: 'rgba(15, 23, 42, 0.02)',
                                                                borderRadius: 1.5,
                                                                border: '1px solid rgba(0,0,0,0.06)'
                                                              }}>
                                                              <Box
                                                                sx={{
                                                                  flex: '1 1 auto',
                                                                  display: 'grid',
                                                                  gridTemplateColumns: '1fr 1fr',
                                                                  gap: 2
                                                                }}>
                                                                {/* SSCC */}
                                                                <Stack direction="column" spacing={0.75}>
                                                                  <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                      fontWeight: 600,
                                                                      color: 'text.secondary',
                                                                      my: 1
                                                                    }}>
                                                                    SSCC
                                                                  </Typography>
                                                                  <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    alignItems="center"
                                                                    flexWrap="wrap">
                                                                    <Typography
                                                                      variant="body2"
                                                                      sx={{
                                                                        fontSize: '0.85rem',
                                                                        fontWeight: '600'
                                                                      }}>
                                                                      {ssccWithAsns.ssccId}
                                                                    </Typography>
                                                                    <Chip
                                                                      label={
                                                                        ssccWithAsns.sscc?.status || 'Unknown Status'
                                                                      }
                                                                      size="small"
                                                                      sx={{
                                                                        height: 20,
                                                                        fontSize: '0.7rem',
                                                                        fontWeight: 600,
                                                                        backgroundColor: 'rgba(0,0,0,0.06)',
                                                                        color: 'text.secondary'
                                                                      }}
                                                                    />
                                                                    {ssccQmMatches.map((record) => (
                                                                      <Chip
                                                                        key={record.qmNumber}
                                                                        label={record.qmNumber}
                                                                        size="small"
                                                                        clickable
                                                                        onClick={(event) => {
                                                                          void openAdminPanelQmDetails(record, event);
                                                                        }}
                                                                        sx={{
                                                                          height: 20,
                                                                          fontSize: '0.7rem',
                                                                          fontWeight: 700,
                                                                          backgroundColor: 'rgba(255, 112, 67, 0.08)',
                                                                          border: '1px solid rgba(255, 112, 67, 0.6)',
                                                                          color: '#e65100',
                                                                          cursor: 'pointer'
                                                                        }}
                                                                      />
                                                                    ))}

                                                                    {ssccWithAsns.sscc?.status ? (
                                                                      <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                          fontSize: '0.75rem',
                                                                          color: 'text.secondary'
                                                                        }}>
                                                                        {getTimeBasedOnStatus({
                                                                          status: ssccWithAsns.sscc
                                                                            ?.status as SsccStatus,
                                                                          sscc: ssccWithAsns.sscc
                                                                        })}
                                                                      </Typography>
                                                                    ) : null}
                                                                  </Stack>
                                                                  <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                      color: 'text.secondary',
                                                                      fontSize: '0.75rem'
                                                                    }}>
                                                                    Nickname: {ssccWithAsns.sscc?.nickname || 'N/A'}
                                                                  </Typography>
                                                                </Stack>

                                                                {/* ASNs for this SSCC */}
                                                                <Stack direction="column">
                                                                  <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                      fontWeight: 600,
                                                                      color: 'text.secondary',
                                                                      my: 1
                                                                    }}>
                                                                    ASNs
                                                                  </Typography>

                                                                  {ssccWithAsns.asns.length === 0 ? (
                                                                    <Typography
                                                                      variant="body2"
                                                                      color="text.secondary"
                                                                      fontStyle="italic">
                                                                      No ASNs
                                                                    </Typography>
                                                                  ) : (
                                                                    <Stack spacing={0.25}>
                                                                      {ssccWithAsns.asns.map((asn, asnIndex) => {
                                                                        const asnKey = buildAsnKey(asn);
                                                                        const asnDuplicateInfo = asnKey
                                                                          ? (asnOccurrences.get(asnKey) ?? [])
                                                                          : [];
                                                                        const isAsnDuplicate =
                                                                          asnDuplicateInfo.length > 1;
                                                                        const asnQmMatches =
                                                                          adminQmMatchesByLookupKey.get(
                                                                            normalizeAdminQmLookupValue(asn.asn)
                                                                          ) ?? [];

                                                                        return (
                                                                          <Box
                                                                            key={`${asn.asn}-${asnIndex}`}
                                                                            sx={{
                                                                              mb: 3,
                                                                              borderRadius: 2,
                                                                              backgroundColor: 'background.default',
                                                                              border: isAsnDuplicate
                                                                                ? '2px solid #d32f2f'
                                                                                : 'none',
                                                                              boxShadow: isAsnDuplicate
                                                                                ? '0 1px 3px rgba(0,0,0,0.08)'
                                                                                : 'none'
                                                                            }}>
                                                                            {isAsnDuplicate && (
                                                                              <Box
                                                                                sx={{
                                                                                  backgroundColor:
                                                                                    'rgba(211, 47, 47, 0.06)',
                                                                                  borderBottom:
                                                                                    '1px solid rgba(211, 47, 47, 0.2)',
                                                                                  px: 2.5,
                                                                                  py: 1.5,
                                                                                  display: 'flex',
                                                                                  alignItems: 'center',
                                                                                  gap: 1.5
                                                                                }}>
                                                                                <ErrorOutlineIcon
                                                                                  sx={{
                                                                                    fontSize: 18,
                                                                                    color: '#c62828'
                                                                                  }}
                                                                                />
                                                                                <Stack spacing={1}>
                                                                                  <Typography
                                                                                    sx={{
                                                                                      fontSize: '0.75rem',
                                                                                      fontWeight: 700,
                                                                                      color: '#c62828',
                                                                                      textTransform: 'uppercase',
                                                                                      letterSpacing: '0.5px'
                                                                                    }}>
                                                                                    Duplicate Detected •{' '}
                                                                                    {asnDuplicateInfo.length}{' '}
                                                                                    occurrences
                                                                                  </Typography>
                                                                                  <Stack
                                                                                    direction="row"
                                                                                    spacing={0.75}
                                                                                    alignItems="center"
                                                                                    flexWrap="wrap">
                                                                                    {asnDuplicateInfo.map(
                                                                                      (info, asnDuplicateInfoIndex) => (
                                                                                        <Chip
                                                                                          key={`${info.tripId}-${info.legIndex}-${info.manifestId}-${asnDuplicateInfoIndex}`}
                                                                                          label={`Trip ${info.tripId || 'N/A'} • Leg ${
                                                                                            Number.isInteger(
                                                                                              info.legIndex
                                                                                            )
                                                                                              ? (info.legIndex ?? 0) + 1
                                                                                              : 'N/A'
                                                                                          } • Manifest ${info.manifestId || 'N/A'} • SSCC ${
                                                                                            info.ssccId || 'N/A'
                                                                                          }`}
                                                                                          size="small"
                                                                                          sx={{
                                                                                            height: 18,
                                                                                            fontSize: '0.65rem',
                                                                                            fontWeight: 600,
                                                                                            backgroundColor:
                                                                                              'rgba(211, 47, 47, 0.1)',
                                                                                            color: '#b71c1c',
                                                                                            border:
                                                                                              '1px solid rgba(211, 47, 47, 0.25)',
                                                                                            '& .MuiChip-label': {
                                                                                              px: 1
                                                                                            }
                                                                                          }}
                                                                                        />
                                                                                      )
                                                                                    )}
                                                                                  </Stack>
                                                                                </Stack>
                                                                              </Box>
                                                                            )}

                                                                            <Box
                                                                              sx={{
                                                                                px: 1.5,
                                                                                py: 0.75,
                                                                                borderRadius: 1,
                                                                                backgroundColor: 'rgba(0,0,0,0.03)',
                                                                                border: '1px solid rgba(0,0,0,0.06)'
                                                                              }}>
                                                                              <Stack
                                                                                direction="row"
                                                                                alignItems="center"
                                                                                flexWrap="wrap"
                                                                                sx={{ gap: 0.75 }}>
                                                                                <Typography
                                                                                  key={asn.asn}
                                                                                  variant="body2"
                                                                                  sx={{
                                                                                    fontSize: '0.85rem',
                                                                                    fontWeight: '600'
                                                                                  }}>
                                                                                  {asn.asn}
                                                                                </Typography>

                                                                                <Chip
                                                                                  label={asn.status || 'Unknown Status'}
                                                                                  size="small"
                                                                                  sx={{
                                                                                    height: 20,
                                                                                    fontSize: '0.7rem',
                                                                                    fontWeight: 600,
                                                                                    backgroundColor: 'rgba(0,0,0,0.06)',
                                                                                    color: 'text.secondary'
                                                                                  }}
                                                                                />
                                                                                {asnQmMatches.map((record) => (
                                                                                  <Chip
                                                                                    key={record.qmNumber}
                                                                                    label={record.qmNumber}
                                                                                    size="small"
                                                                                    clickable
                                                                                    onClick={(event) => {
                                                                                      void openAdminPanelQmDetails(
                                                                                        record,
                                                                                        event
                                                                                      );
                                                                                    }}
                                                                                    sx={{
                                                                                      height: 20,
                                                                                      fontSize: '0.7rem',
                                                                                      fontWeight: 700,
                                                                                      backgroundColor:
                                                                                        'rgba(255, 112, 67, 0.08)',
                                                                                      border:
                                                                                        '1px solid rgba(255, 112, 67, 0.6)',
                                                                                      color: '#e65100',
                                                                                      cursor: 'pointer'
                                                                                    }}
                                                                                  />
                                                                                ))}
                                                                              </Stack>
                                                                            </Box>
                                                                          </Box>
                                                                        );
                                                                      })}
                                                                    </Stack>
                                                                  )}
                                                                </Stack>
                                                              </Box>
                                                              <Box
                                                                sx={{
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  color: 'text.secondary'
                                                                }}>
                                                                <ChevronRightIcon sx={{ fontSize: 20 }} />
                                                              </Box>
                                                            </Box>
                                                          </Box>
                                                        );
                                                      }
                                                    )}
                                                  </Stack>
                                                </Box>
                                                <Divider
                                                  sx={{
                                                    borderColor: 'rgba(0,0,0,0.08)',
                                                    my: 2
                                                  }}
                                                />

                                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                                  <Tooltip
                                                    title={
                                                      ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                        tripDisplayStatus
                                                      )
                                                        ? `Trip status is ${tripDisplayStatus}, please add SSCC in Bustle.`
                                                        : ''
                                                    }
                                                    arrow>
                                                    <span>
                                                      <Button
                                                        variant="outlined"
                                                        size="small"
                                                        disabled={!canManageManifestActions}
                                                        startIcon={
                                                          <AddIcon
                                                            sx={{
                                                              fontSize: 16
                                                            }}
                                                          />
                                                        }
                                                        onClick={() => {
                                                          const ssccStatus =
                                                            mapTripLegStatusToSsccStatus(tripDisplayStatus);

                                                          const asnStatus = mapSsccStatusToAsnStatus({
                                                            ssccStatus,
                                                            manifestStatus: manifest.status
                                                          });

                                                          handleOpenAddSSCCToManifestDialog({
                                                            manifest: transformedManifest,
                                                            tripDisplayStatus,
                                                            ssccStatus,
                                                            asnStatus,
                                                            tripId: trip.id,
                                                            legId: leg.id
                                                          });
                                                        }}
                                                        sx={{
                                                          minWidth: 180,
                                                          px: 2,
                                                          py: 0.75,
                                                          height: 36,
                                                          fontWeight: 600,
                                                          fontSize: '0.8rem',
                                                          textTransform: 'none',
                                                          borderColor: 'rgba(25, 118, 210, 0.6)',
                                                          color: '#1976d2',
                                                          borderRadius: 1.5,
                                                          '&:hover': {
                                                            borderColor: '#1976d2',
                                                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                          }
                                                        }}>
                                                        Add SSCC To Manifest
                                                      </Button>
                                                    </span>
                                                  </Tooltip>
                                                  <Tooltip
                                                    title={
                                                      ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                        tripDisplayStatus
                                                      )
                                                        ? `Trip status is ${tripDisplayStatus}, you can not mark completed.`
                                                        : ''
                                                    }
                                                    arrow>
                                                    <span>
                                                      <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={
                                                          <CheckCircleIcon
                                                            sx={{
                                                              fontSize: 16
                                                            }}
                                                          />
                                                        }
                                                        onClick={() =>
                                                          handleOpenMarkCompletedManifestDialog({
                                                            tripDisplayStatus,
                                                            transformedManifest,
                                                            manifestStatus: manifest.status,
                                                            tripId: trip.id,
                                                            legId: leg.id
                                                          })
                                                        }
                                                        disabled={!canManageManifestActions}
                                                        sx={{
                                                          minWidth: 200,
                                                          px: 2,
                                                          py: 0.75,
                                                          height: 36,
                                                          fontWeight: 600,
                                                          fontSize: '0.8rem',
                                                          textTransform: 'none',
                                                          borderColor: 'rgba(76, 175, 80, 0.6)',
                                                          color: '#4caf50',
                                                          borderRadius: 1.5,
                                                          '&:hover': {
                                                            borderColor: '#4caf50',
                                                            backgroundColor: 'rgba(76, 175, 80, 0.04)'
                                                          }
                                                        }}>
                                                        Mark Manifest Completed
                                                      </Button>
                                                    </span>
                                                  </Tooltip>
                                                  <Tooltip
                                                    title={
                                                      ![TripLegStatus.arrived, TripLegStatus.completed].includes(
                                                        tripDisplayStatus
                                                      )
                                                        ? `Trip status is ${tripDisplayStatus}, please delete manifest in Bustle.`
                                                        : ''
                                                    }
                                                    arrow>
                                                    <span>
                                                      <Button
                                                        variant="outlined"
                                                        size="small"
                                                        disabled={!canManageManifestActions}
                                                        startIcon={
                                                          <DeleteIcon
                                                            sx={{
                                                              fontSize: 16
                                                            }}
                                                          />
                                                        }
                                                        onClick={() => {
                                                          handleOpenDeleteManifestDialog({
                                                            tripDisplayStatus,
                                                            manifestStatus: manifest.status,
                                                            transformedManifest,
                                                            tripId: trip.id,
                                                            legId: leg.id
                                                          });
                                                        }}
                                                        sx={{
                                                          minWidth: 160,
                                                          px: 2,
                                                          py: 0.75,
                                                          height: 36,
                                                          fontWeight: 600,
                                                          fontSize: '0.8rem',
                                                          textTransform: 'none',
                                                          borderColor: 'rgba(211, 47, 47, 0.6)',
                                                          color: '#c62828',
                                                          borderRadius: 1.5,
                                                          '&:hover': {
                                                            borderColor: '#c62828',
                                                            backgroundColor: 'rgba(211, 47, 47, 0.06)'
                                                          }
                                                        }}>
                                                        Delete Manifest
                                                      </Button>
                                                    </span>
                                                  </Tooltip>
                                                </Stack>
                                              </Box>
                                            </Box>
                                          </Box>
                                        );
                                      })
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}

                  {/* Pagination */}

                  <Box sx={{ height: '3rem' }} />

                  <Box
                    sx={{
                      position: 'fixed',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      zIndex: 10,
                      backgroundColor: 'background.default',
                      borderTop: '1px solid rgba(0,0,0,0.08)',
                      py: 1.5
                    }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                          Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                          {Math.min(currentPage * PAGE_SIZE, filteredTripSheets.length)} of {filteredTripSheets.length}{' '}
                          trip sheets
                        </Typography>
                        {expandedTrips.size > 0 && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setExpandedTrips(new Set())}
                            sx={{ fontSize: '0.8rem', textTransform: 'none', color: 'text.secondary', py: 0 }}>
                            Collapse All
                          </Button>
                        )}
                      </Stack>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        siblingCount={2}
                        boundaryCount={2}
                      />
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* 00 SSCC Actions Drawer */}
      {ssccActionsOpen && (ssccActionsContext || ssccActionsPayload) && (
        <SsccActionsDrawer
          open={ssccActionsOpen}
          onClose={handleCloseSsccActions}
          ssccs={ssccs}
          ssccActionsContext={ssccActionsContext}
          ssccActionsPayload={ssccActionsPayload}
          asnOccurrences={asnOccurrences}
          ssccOccurrences={ssccOccurrences}
          handleOpenAddASNToSSCCDialog={handleOpenAddASNToSSCCDialog}
          handleOpenMarkCompletedSSCCDialog={handleOpenMarkCompletedSSCCDialog}
          handleOpenDeleteUnassignedSSCCDialog={handleOpenDeleteUnassignedSSCCDialog}
          handleOpenDeleteSSCCDialog={handleOpenDeleteSSCCDialog}
          handleOpenDeleteASNDialog={handleOpenDeleteASNDialog}
          getTimeBasedOnStatus={getTimeBasedOnStatus}
          qmMatchesByLookupKey={adminQmMatchesByLookupKey}
          openQmDetails={openAdminPanelQmDetails}
        />
      )}

      <QmDetailDialog
        open={Boolean(adminPanelSelectedQm) || isAdminPanelQmDetailsLoading}
        onClose={closeAdminPanelQmDetails}
        model={adminPanelQmDetailDialogModel}
      />

      {/* 01 Add SSCC to Manifest Dialog */}
      {openAddSSCCToManifestDialog && addSSCCToManifestData && (
        <AddSSCCToManifestDialog
          open={openAddSSCCToManifestDialog}
          selectedManifest={addSSCCToManifestData}
          availableSSCCs={openStagedSsccs.filter(
            (sscc) => !addSSCCToManifestData.manifest.ssccsWithAsns.map(({ ssccId }) => ssccId).includes(sscc.id)
          )}
          selectedSSCCId={addSSCCToManifestSelectedId}
          ssccWeight={addSSCCToManifestWeight}
          withReceiveManifest={addSSCCToManifestReceiveManifest}
          isProcessing={isProcessing}
          onClose={resetAddSSCCToManifestState}
          onSSCCIdChange={setAddSSCCToManifestSelectedId}
          onWeightChange={setAddSSCCToManifestWeight}
          onReceiveManifestChange={setAddSSCCToManifestReceiveManifest}
          onConfirm={handleAddSSCCToManifest}
          onAlert={alert}
        />
      )}

      {/* 02 Mark as Completed Manifest Dialog */}
      {openCompleteManifestDialog && completeManifestData && (
        <MarkCompletedManifestDialog
          open={openCompleteManifestDialog}
          selectedManifest={completeManifestData}
          isProcessing={isProcessing}
          onClose={resetCompleteManifestState}
          onConfirm={handleMarkAsCompletedManifest}
        />
      )}

      {/* 03 Delete Manifest Dialog */}
      {openDeleteManifestDialog && deleteManifestData && (
        <DeleteManifestDialog
          open={openDeleteManifestDialog}
          selectedManifest={deleteManifestData}
          isProcessing={isProcessing}
          onClose={resetDeleteManifestState}
          onConfirm={handleDeleteManifest}
        />
      )}

      {/* 04A Add ASN to SSCC Dialog */}
      {openAddASNToSSCCDialog && addASNToSSCCData && (
        <AddASNToSSCCDialog
          open={openAddASNToSSCCDialog}
          selectedManifest={addASNToSSCCData}
          ssccId={addASNToSSCCData?.ssccId || ''}
          availableASNs={availableASNsForAssignedSSCC}
          selectedASNId={addASNToSSCCSelectedId}
          selectedReceiptEvent={addASNToSSCCReceiptEvent}
          receiptedEvents={addASNToSSCCData?.sscc?.sscc?.receiptedEvents || []}
          withReceiveManifest={addASNToSSCCReceiveManifest}
          isProcessing={isProcessing}
          onClose={resetAddASNToSSCCState}
          onASNIdChange={setAddASNToSSCCSelectedId}
          onReceiptEventChange={setAddASNToSSCCReceiptEvent}
          onReceiveManifestChange={setAddASNToSSCCReceiveManifest}
          onConfirm={(actionReason) => {
            handleAddASNToSSCC({
              selectedManifestSSCC: addASNToSSCCData,
              tripSheets,
              asn: selectedAssignedASN,
              ssccId: addASNToSSCCData.ssccId || '',
              actionReason,
              tripId: addASNToSSCCData?.tripId,
              legId: addASNToSSCCData?.legId,
              manifestId: addASNToSSCCData?.manifest?.id,
              asnStatus:
                selectedAssignedASN?.status === AsnStatus.available ? AsnStatus.available : addASNToSSCCData.asnStatus,
              receiptedEventIndex: addASNToSSCCData?.sscc?.sscc?.receiptedEvents?.findIndex(
                (event, index) => `${index + 1}. ${event.name} - ${event.site}` === addASNToSSCCReceiptEvent
              ),
              withReceiveManifestCheck: addASNToSSCCData?.manifest?.status === 'Completed'
            });
          }}
        />
      )}

      {/* 04B Add ASN to Unassigned SSCC Dialog */}
      {openAddASNToSSCCDialog && addASNToUnassignedSSCCData && (
        <AddASNForUnassignedSSCCDialog
          open={openAddASNToSSCCDialog}
          ssccId={addASNToUnassignedSSCCData?.ssccId || ''}
          asnStatus={addASNToUnassignedSSCCData?.ssccStatus || ''}
          availableASNs={availableASNsForUnassignedSSCC}
          selectedASNId={addASNToSSCCSelectedId}
          selectedReceiptEvent={addASNToSSCCReceiptEvent}
          receiptedEvents={addASNToUnassignedSSCCData?.sscc?.receiptedEvents || []}
          isProcessing={isProcessing}
          onClose={resetAddASNToSSCCState}
          onASNIdChange={setAddASNToSSCCSelectedId}
          onReceiptEventChange={setAddASNToSSCCReceiptEvent}
          onConfirm={(actionReason) => {
            handleAddASNToUnassignedSSCC({
              asn: selectedUnassignedASN,
              ssccId: addASNToUnassignedSSCCData?.ssccId || '',
              actionReason,
              asnStatus:
                selectedUnassignedASN?.status === AsnStatus.available
                  ? AsnStatus.available
                  : addASNToUnassignedSSCCData.ssccStatus,
              receiptedEventIndex: addASNToUnassignedSSCCData?.sscc?.receiptedEvents?.findIndex(
                (event, index) => `${index + 1}. ${event.name} - ${event.site}` === addASNToSSCCReceiptEvent
              )
            });
          }}
        />
      )}

      {/* 05A Mark as Completed SSCC Dialog */}
      {openCompleteSSCCDialog && completeSSCCData && (
        <MarkCompletedSSCCDialog
          open={openCompleteSSCCDialog}
          selectedManifest={completeSSCCData}
          ssccWeight={completeSSCCWeight}
          showWeightInput={!completeSSCCData?.sscc?.sscc?.weight}
          isProcessing={isProcessing}
          onClose={resetCompleteSSCCState}
          onWeightChange={setCompleteSSCCWeight}
          onConfirm={handleMarkAsCompletedSSCC}
        />
      )}

      {/* 05B Mark as Completed Unassigned SSCC Dialog */}
      {openCompleteSSCCDialog && completeUnassignedSSCCData && (
        <MarkCompletedUnassignedSSCCDialog
          open={openCompleteSSCCDialog}
          ssccId={completeUnassignedSSCCData.ssccId}
          ssccStatus={completeUnassignedSSCCData.ssccStatus}
          ssccWeight={completeSSCCWeight}
          showWeightInput={!completeUnassignedSSCCData.sscc?.weight}
          isProcessing={isProcessing}
          onClose={resetCompleteSSCCState}
          onWeightChange={setCompleteSSCCWeight}
          onConfirm={handleMarkAsCompletedUnassignedSSCC}
        />
      )}

      {/* 06 Delete SSCC Dialog */}
      {openDeleteSSCCDialog && deleteSSCCData && (
        <DeleteSSCCDialog
          open={openDeleteSSCCDialog}
          selectedManifest={deleteSSCCData}
          isProcessing={isProcessing}
          onClose={resetDeleteSSCCState}
          onConfirm={handleDeleteSSCC}
        />
      )}

      {openDeleteUnassignedSSCCDialog && deleteUnassignedSSCCData && (
        <DeleteUnassignedSSCCDialog
          open={openDeleteUnassignedSSCCDialog}
          ssccId={deleteUnassignedSSCCData.ssccId}
          ssccStatus={deleteUnassignedSSCCData.ssccStatus}
          isProcessing={isProcessing}
          onClose={resetDeleteUnassignedSSCCState}
          onConfirm={handleDeleteUnassignedSSCC}
        />
      )}

      {/* 07 Delete ASN Dialog */}
      {openDeleteASNDialog && deleteASNData && (
        <DeleteASNDialog
          open={openDeleteASNDialog}
          selectedASN={deleteASNData}
          selectedTransferSSCCId={deleteASNTransferSSCCId}
          selectedTransferReceiptedEvent={deleteASNTransferReceiptedEvent}
          availableTransferSSCCs={eligibleTransferSSCCs
            .filter((sscc) => sscc.id !== deleteASNData.ssccId)
            .sort((a, b) =>
              String(b.id).localeCompare(String(a.id), undefined, { numeric: true, sensitivity: 'base' })
            )}
          isProcessing={isProcessing}
          onClose={resetDeleteASNState}
          onTransferSSCCIdChange={(ssccId) => {
            setDeleteASNTransferSSCCId(ssccId);
            setDeleteASNTransferReceiptedEvent('');
          }}
          onTransferReceiptedEventChange={setDeleteASNTransferReceiptedEvent}
          onConfirm={handleDeleteASN}
        />
      )}

      {/* Bustle Confirmation Dialog */}
      {openBustleDialog && (
        <ConfirmationDialog
          open={!!openBustleDialog}
          title="Action Required"
          message={openBustleDialog?.message}
          messageDetail={openBustleDialog?.messageDetail}
          onClose={() => setOpenBustleDialog(null)}
        />
      )}

      {/* Coupa Confirmation Dialog */}
      {openCoupaDialog && (
        <ConfirmationDialog
          open={openCoupaDialog}
          title="Action Required"
          message="Access Coupa and check Inventory Transfer"
          messageDetail="Please access Coupa to check already transferred inventory related to this deletion."
          onClose={() => setOpenCoupaDialog(false)}
        />
      )}
    </Stack>
  );
}
